import React, { useEffect, useState, Fragment } from 'react';
import { ArrowLeft, Package, Barcode, CheckCircle2, AlertCircle, Truck, User, MapPin, Phone, CreditCard, Info, XCircle, ChevronRight, Scan, Clock, Globe, Smartphone, ShieldCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { deriveFulfillmentProfile, Order, OrderLine, OrderStatus, PaymentType } from './OrderQueue';
import { OperationalSettings } from './SettingsPanel';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
interface OrderDetailsProps {
  order: Order;
  settings: OperationalSettings;
  onBack: () => void;
  onUpdateStatus: (status: OrderStatus, updates?: Partial<Order>, systemActor?: boolean) => void;
  onUpdateLines: (lines: OrderLine[]) => void;
  onUpdatePaymentMethod?: (paymentType: PaymentType) => void;
  readOnly?: boolean;
  initialShowRejectModal?: boolean;
  initialIsBulkScanning?: boolean;
  initialConfirmItem?: OrderLine | null;
  initialShowPartialModal?: boolean;
  initialScanningItem?: OrderLine | null;
  initialShowGlobalScanModal?: boolean;
  initialShowHoldModal?: boolean;
  initialShowOtpModal?: boolean;
}
export function OrderDetails({
  order,
  settings,
  onBack,
  onUpdateStatus,
  onUpdateLines,
  initialShowRejectModal = false,
  initialIsBulkScanning = false,
  initialConfirmItem = null,
  initialShowPartialModal = false,
  initialScanningItem = null,
  initialShowGlobalScanModal = false,
  initialShowHoldModal = false,
  initialShowOtpModal = false,
  readOnly = false,
  onUpdatePaymentMethod
}: OrderDetailsProps) {
  const [isBulkScanning, setIsBulkScanning] = useState(initialIsBulkScanning);
  const [isEditingPayment, setIsEditingPayment] = useState(false);
  const [isWaitingConfirmation, setIsWaitingConfirmation] = useState(false);
  const [showAggregatorRejectionModal, setShowAggregatorRejectionModal] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [tempSubstitution, setTempSubstitution] = useState('');
  const items = order.lines;
  const fulfillmentProfile = deriveFulfillmentProfile(order);
  const [scanningItem, setScanningItem] = useState<OrderLine | null>(null);
  const [showGlobalScanModal, setShowGlobalScanModal] = useState(initialShowGlobalScanModal);
  const [globalScanValue, setGlobalScanValue] = useState('');
  const [manualQty, setManualQty] = useState('');
  const [tempSerial, setTempSerial] = useState('');
  const [tempLot, setTempLot] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(initialShowRejectModal);
  const [showOrderRejectionModal, setShowOrderRejectionModal] = useState(false);
  const [orderRejectionReason, setOrderRejectionReason] = useState('');
  const [orderRejectionCustomReason, setOrderRejectionCustomReason] = useState('');
  const [showPartialModal, setShowPartialModal] = useState(initialShowPartialModal);
  const [showHoldModal, setShowHoldModal] = useState(initialShowHoldModal);
  const [showPatientShareModal, setShowPatientShareModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(initialShowOtpModal);
  const [showRxModal, setShowRxModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showReturnReceiptModal, setShowReturnReceiptModal] = useState(false);
  const [returnItemStatus, setReturnItemStatus] = useState<Record<string, {
    received: boolean;
    condition: 'Good' | 'Damaged' | 'Missing';
    note: string;
    missingQuantity: number;
  }>>({});
  const [returnReason, setReturnReason] = useState('');
  const [returnMethod, setReturnMethod] = useState<'Driver' | 'Customer'>('Customer');
  const [otpValue, setOtpValue] = useState('');
  const [nationalIdValue, setNationalIdValue] = useState('');
  const [pickupMobileValue, setPickupMobileValue] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  useEffect(() => {
    let interval: any;
    if (showOtpModal && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [showOtpModal, resendTimer]);
  const handleResendOtp = () => {
    setResendTimer(60);
    setCanResend(false);
    import('sonner').then(({
      toast
    }) => toast.success('OTP has been resent to the customer'));
  };
  const handleSendWhatsApp = () => {
    import('sonner').then(({
      toast
    }) => toast.success('OTP sent via WhatsApp successfully'));
  };
  const [rejectionReason, setRejectionReason] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [holdCustomReason, setHoldCustomReason] = useState('');
  const [returnCustomReason, setReturnCustomReason] = useState('');
  useEffect(() => {
    setShowRejectModal(initialShowRejectModal);
  }, [initialShowRejectModal]);
  useEffect(() => {
    setIsBulkScanning(initialIsBulkScanning);
  }, [initialIsBulkScanning]);
  useEffect(() => {
    setShowPartialModal(initialShowPartialModal);
  }, [initialShowPartialModal]);
  useEffect(() => {
    setShowGlobalScanModal(initialShowGlobalScanModal);
  }, [initialShowGlobalScanModal]);
  useEffect(() => {
    setShowHoldModal(initialShowHoldModal);
  }, [initialShowHoldModal]);
  useEffect(() => {
    setShowOtpModal(initialShowOtpModal);
  }, [initialShowOtpModal]);
  useEffect(() => {
    if (initialScanningItem) {
      setScanningItem(initialScanningItem);
      setShowGlobalScanModal(true);
      setManualQty(initialScanningItem.pickedQuantity > 0 ? initialScanningItem.pickedQuantity.toString() : initialScanningItem.quantity.toString());
    }
  }, [initialScanningItem]);
  const handleConfirmPartial = () => {
    setShowPartialModal(false);
    if (fulfillmentProfile.requiresExplicitPartialConfirmation) {
      setIsWaitingConfirmation(true);
    } else {
      onUpdateStatus(fulfillmentProfile.nextReadyStatus);
    }
  };
  const resolveCustomerPartialConfirmation = (confirmed: boolean) => {
    setIsWaitingConfirmation(false);
    if (confirmed) {
      onUpdateStatus(fulfillmentProfile.nextReadyStatus);
    } else {
      onUpdateStatus('Cancelled', {
        rejectionReason: 'Customer declined partial fulfillment'
      });
    }
  };
  const handleMarkUnavailable = (itemId: string) => {
    onUpdateLines(items.map(item => item.id === itemId ? {
      ...item,
      pickedQuantity: 0,
      selectedLot: undefined,
      selectedLotNumber: undefined,
      serialNumbers: [],
      status: 'Unavailable'
    } : item));
    setScanningItem(null);
    setShowGlobalScanModal(false);
    setTempLot('');
    setTempSerial('');
    setManualQty('');
  };
  const handleRestoreUnavailable = (itemId: string) => {
    onUpdateLines(items.map(item => item.id === itemId ? {
      ...item,
      status: 'Pending'
    } : item));
  };
  const cancelReasons = settings.cancelReasons;
  const rejectReasons = settings.rejectReasons;
  const holdReasons = settings.holdReasons;
  const returnReasons = settings.returnReasons;
  const cancelReasonRequiresText = cancelReasons.find(reason => reason.label === rejectionReason)?.requiresFreeText ?? false;
  const rejectReasonRequiresText = rejectReasons.find(reason => reason.label === orderRejectionReason)?.requiresFreeText ?? false;
  const holdReasonRequiresText = holdReasons.find(reason => reason.label === holdReason)?.requiresFreeText ?? false;
  const returnReasonRequiresText = returnReasons.find(reason => reason.label === returnReason)?.requiresFreeText ?? false;
  const handlePickItem = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    setScanningItem(item);
    setShowGlobalScanModal(true);
    setManualQty(item.pickedQuantity > 0 ? item.pickedQuantity.toString() : item.quantity.toString());
    setTempLot(item.selectedLotNumber || '');
    setTempSubstitution(item.dispensedTradeDrugCode || '');
  };
  const handleGlobalScan = (input: string) => {
    const parts = input.split('*');
    const scanValue = parts[0].trim();
    const scanQty = parts.length > 1 ? parseInt(parts[1]) : 1;
    const item = items.find(i => i.sku === scanValue || i.isSerialized && scanValue.startsWith('SN-') || i.lotNumbers && i.lotNumbers.some(l => l.number === scanValue));
    if (item) {
      setScanningItem(item);
      setManualQty(item.pickedQuantity > 0 ? Math.min(item.pickedQuantity + scanQty, item.quantity).toString() : Math.min(scanQty, item.quantity).toString());
      if (item.isSerialized && scanValue.startsWith('SN-')) {
        setTempSerial(scanValue);
      }
      if (item.lotNumbers?.some(l => l.number === scanValue)) {
        setTempLot(scanValue);
      }
      setTempSubstitution(item.dispensedTradeDrugCode || '');
      import('sonner').then(({
        toast
      }) => {
        toast.success(`Item Identified: ${item.name}`);
      });
    } else {
      import('sonner').then(({
        toast
      }) => {
        toast.error(`Item ${scanValue} not found in this order`);
      });
    }
    setGlobalScanValue('');
  };
  const handleConfirmItemFulfillment = () => {
    if (!scanningItem) return;
    const qty = parseInt(manualQty) || 0;
    const finalQty = Math.min(qty, scanningItem.quantity);
    onUpdateLines(items.map(item => item.id === scanningItem.id ? {
      ...item,
      pickedQuantity: finalQty,
      selectedLot: tempLot,
      selectedLotNumber: tempLot,
      dispensedTradeDrugCode: tempSubstitution || item.dispensedTradeDrugCode,
      serialNumbers: tempSerial && item.isSerialized && !item.serialNumbers.includes(tempSerial) ? [...item.serialNumbers, tempSerial] : item.serialNumbers,
      status: finalQty === item.quantity ? 'Picked' : finalQty > 0 ? 'Partially Picked' : 'Pending'
    } : item));
    // If we are in bulk scan mode, we stay in the scan modal but clear the current item
    if (isBulkScanning) {
      setScanningItem(null);
      setTempSerial('');
      setTempLot('');
      setTempSubstitution('');
      setManualQty('');
      // showGlobalScanModal remains true
    } else {
      setScanningItem(null);
      setShowGlobalScanModal(false);
      setTempSerial('');
      setTempLot('');
      setTempSubstitution('');
      setManualQty('');
    }
  };
  const getTherapyEndDate = (genericCode: string) => {
    const durationDays = order.prescription?.activities.find(activity => activity.genericCode === genericCode)?.durationDays;
    if (!durationDays) return null;
    const start = new Date(order.createdAt);
    return Number.isNaN(start.getTime()) ? null : new Date(start.getTime() + durationDays * 24 * 60 * 60 * 1000);
  };
  const allPicked = items.every(item => item.status === 'Picked');
  return <div className="max-w-6xl mx-auto space-y-6 pb-20" data-id="element-26" data-mp="20uz8etqszdgz">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-27" data-mp="1vy0h0iyixkmh">
        <div className="flex items-center gap-4" data-id="element-28" data-mp="1r5pj1lncg8wk">
          <button onClick={onBack} className="p-2 hover:bg-white rounded-full transition-colors border border-transparent hover:border-slate-200" data-id="element-29" data-mp="1r4ebrbuowj0p">
            <ArrowLeft className="w-5 h-5 text-slate-600" data-id="element-30" data-mp="2jn1w2p1v3u0q" />
          </button>
          <div data-id="element-31" data-mp="2eva204jhz65u">
            <div className="flex items-center gap-3" data-id="element-32" data-mp="2euifdj7voirp">
              <h2 className="text-2xl font-bold text-slate-900" data-id="element-33" data-mp="cxztrqffykgp">
                Order #{order.id}
              </h2>
              <div className={cn('px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border', order.paymentType === 'COD' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100')} data-id="element-34" data-mp="h04kohei92zn">
                {order.paymentType}
              </div>
            </div>
            <div className="mt-0.5 flex items-center gap-2" data-id="element-35" data-mp="3ufannfvspjir">
              <p className="text-xs text-slate-500" data-id="element-36" data-mp="3gezzwlps8l1t">Placed on {order.createdAt}</p>
              <span className="text-xs text-slate-300" data-id="element-37" data-mp="3ldtcmbx8w80x">•</span>
              <p className="text-xs font-medium text-slate-500" data-id="element-38" data-mp="3lecsln3c4mcb">Accept by {new Date(order.acceptanceDeadline).toLocaleString()}</p>
            </div>
          </div>
        </div>

        {!readOnly && <div className="flex items-center gap-3" data-id="element-39" data-mp="1jloo7aeyfsuy">
            {['Pending', 'Accepted', 'Picking', 'Searching for Driver', 'Driver Assigned', 'Ready for Pickup', 'Delivery Attempt Failed'].includes(order.status) && <div className="flex items-center gap-2" data-id="element-40" data-mp="1xs04l62jjjbh">
                <button onClick={() => setShowRejectModal(true)} className="px-4 py-2 text-red-600 font-bold text-sm hover:bg-red-50 rounded-xl transition-colors" data-id="element-41" data-mp="1xqf8vd4hpuc1">
                  Cancel Order
                </button>
                <button onClick={() => setShowHoldModal(true)} className="px-4 py-2 text-slate-600 font-bold text-sm hover:bg-slate-50 rounded-xl transition-colors" data-id="element-42" data-mp="2lhufhi3auxt3">
                  Hold Order
                </button>
              </div>}

            {order.status === 'Held' && <div className="flex gap-3" data-id="element-43" data-mp="n411g1nup6eo">
                <button onClick={() => setShowRejectModal(true)} className="px-4 py-2 text-red-600 font-bold text-sm hover:bg-red-50 rounded-xl transition-colors" data-id="element-44" data-mp="ojwsxledpd4y">
                  Cancel Order
                </button>
                <button onClick={() => onUpdateStatus(order.previousStatus || 'Pending')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all" data-id="element-45" data-mp="12iwegk6lhbww">
                  Resume Order
                </button>
              </div>}

            {order.status === 'Pending' && <div className="flex items-center gap-3" data-id="element-46" data-mp="jkslaz73rngq">
                {fulfillmentProfile.requiresPrescriptionReview && order.prescription && <button onClick={() => setShowRxModal(true)} className="px-6 py-2 bg-purple-600 text-white font-bold rounded-xl shadow-lg shadow-purple-200 hover:bg-purple-700 transition-colors flex items-center gap-2" data-id="element-47" data-mp="3n940upzeuyct">
                    <Globe className="w-4 h-4" data-id="element-48" data-mp="3aof3avtfkd15" />
                    Review eRx
                  </button>}
                <button onClick={() => setShowOrderRejectionModal(true)} className="rounded-xl border border-rose-200 bg-white px-4 py-2 text-sm font-bold text-rose-700 transition-colors hover:bg-rose-50" data-id="element-49" data-mp="31ptn37bfla3j">
                  Reject Order
                </button>
                <button disabled={fulfillmentProfile.requiresPrescriptionReview && !order.prescription?.verified} onClick={() => {
            if (fulfillmentProfile.requiresPatientShareCollection) {
              onUpdateStatus('Accepted');
              setShowPatientShareModal(true);
            } else if (fulfillmentProfile.requiresPaymentLink) {
              onUpdateStatus('Awaiting Payment');
            } else {
              onUpdateStatus('Accepted');
            }
          }} className="rounded-xl bg-blue-600 px-6 py-2 font-bold text-white shadow-lg shadow-blue-200 transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-50" data-mp="2n0e2k92hu0wa">
                  Accept Order
                </button>
              </div>}

            {order.status === 'Accepted' && (fulfillmentProfile.requiresPatientShareCollection ? <button onClick={() => setShowPatientShareModal(true)} className="flex items-center gap-2 rounded-xl bg-violet-600 px-6 py-2 font-bold text-white shadow-lg shadow-violet-200 transition-colors hover:bg-violet-700" data-id="element-51" data-mp="28d6zepj9277y">
                  <CreditCard className="h-4 w-4" data-id="element-52" data-mp="35u4cy7sl68qy" />
                  Collect Patient Share
                </button> : <button onClick={() => onUpdateStatus('Picking')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-53" data-mp="ud2i41sfd8kd">
                  <Package className="w-4 h-4" data-id="element-54" data-mp="z53ppaeneb42" />
                  Start Picking
                </button>)}

            {order.status === 'Picking' && <>
                {allPicked ? <button onClick={() => onUpdateStatus(order.channel === 'Walk-in' ? 'Delivered' : fulfillmentProfile.nextReadyStatus)} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-6 py-2 font-bold text-white shadow-lg shadow-emerald-200 transition-colors hover:bg-emerald-700" data-id="element-55" data-mp="6i99ipov5xwe">
                    <CheckCircle2 className="h-4 w-4" data-id="element-56" data-mp="1qx1gqod0tida" />
                    {order.channel === 'Walk-in' ? 'Hand over at counter' : 'Ready to Pick'}
                  </button> : <button onClick={() => fulfillmentProfile.allowsPartialFulfillment ? setShowPartialModal(true) : setShowAggregatorRejectionModal(true)} className="flex items-center gap-2 rounded-xl bg-amber-500 px-6 py-2 font-bold text-white shadow-lg shadow-amber-200 transition-colors hover:bg-amber-600" data-id="element-57" data-mp="2fx7jdi3ifgmv">
                    <AlertCircle className="h-4 w-4" data-id="element-58" data-mp="2trhxl5hvrseo" />
                    {fulfillmentProfile.allowsPartialFulfillment ? 'Confirm Scan' : 'Resolve unavailable item'}
                  </button>}
              </>}

            {order.status === 'Awaiting Partner Courier' && fulfillmentProfile.usesPartnerCourier && <button onClick={() => onUpdateStatus('Out for Delivery', undefined, true)} className="flex items-center gap-2 rounded-xl bg-cyan-600 px-6 py-2 font-bold text-white shadow-lg shadow-cyan-200 transition-colors hover:bg-cyan-700" data-id="element-59" data-mp="19ac15elq5py5">
                <Truck className="h-4 w-4" data-id="element-60" data-mp="110eatfliolbq" />
                Handed to partner courier
              </button>}

            {order.status === 'Searching for Driver' && !fulfillmentProfile.usesPartnerCourier && !fulfillmentProfile.requiresPickupOtp && !fulfillmentProfile.requiresNationalIdVerification && <button onClick={() => onUpdateStatus('Driver Assigned', {
          courierPhone: order.courierName ? '+966 9200 12345' : undefined,
          driverInfo: {
            name: 'Ahmed Al-Fahad',
            phone: '+966 55 000 1111',
            vehiclePlate: 'ABC 1234',
            rating: 4.8,
            avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Ahmed'
          }
        })} className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-2 font-bold text-white shadow-lg shadow-blue-200 transition-colors hover:bg-blue-700" data-id="element-61" data-mp="1ooxne7blnk9m">
                <Truck className="h-4 w-4" data-id="element-62" data-mp="2qznb8lsnazlc" />
                Simulate Driver Assignment
              </button>}

            {order.status === 'Driver Assigned' && !fulfillmentProfile.usesPartnerCourier && <button onClick={() => onUpdateStatus('Out for Delivery')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-63" data-mp="3eqsrv36etvwp">
                <Truck className="w-4 h-4" data-id="element-64" data-mp="35sh1n29gh04s" />
                Driver Picked Up
              </button>}

            {order.status === 'Out for Delivery' && <div className="flex gap-3" data-id="element-65" data-mp="1ucshsjri0h87">
                <button onClick={() => onUpdateStatus('Delivery Attempt Failed', undefined, fulfillmentProfile.usesPartnerCourier)} className="rounded-xl px-4 py-2 text-sm font-bold text-rose-600 transition-colors hover:bg-rose-50" data-id="element-66" data-mp="1pc4i79p7es2d">
                  {fulfillmentProfile.usesPartnerCourier ? 'Partner reported failed' : 'Delivery Failed'}
                </button>
                <button onClick={() => onUpdateStatus('Delivered', undefined, fulfillmentProfile.usesPartnerCourier)} className="rounded-xl bg-emerald-600 px-6 py-2 font-bold text-white shadow-lg shadow-emerald-200 transition-colors hover:bg-emerald-700" data-id="element-67" data-mp="1txlvlaoigqum">
                  {fulfillmentProfile.usesPartnerCourier ? 'Partner confirmed delivery' : 'Confirm Delivery'}
                </button>
              </div>}

            {order.status === 'Delivery Attempt Failed' && <div className="flex gap-3" data-id="element-68" data-mp="dkexqndoj2j6">
                <button onClick={() => onUpdateStatus('Searching for Driver')} className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-2 font-bold text-white shadow-lg shadow-blue-200 transition-colors hover:bg-blue-700" data-id="element-69" data-mp="ihx4c74855o0">
                  <Truck className="h-4 w-4" data-id="element-70" data-mp="5kiozs39lobr" />
                  Retry delivery
                </button>
                <button onClick={() => onUpdateStatus('Held', {
            holdReason: 'Delivery failed'
          })} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-50" data-id="element-71" data-mp="fgbxn0dv4xo9">
                  Return to branch
                </button>
              </div>}

            {order.status === 'Ready for Pickup' && (fulfillmentProfile.requiresPickupOtp || fulfillmentProfile.requiresNationalIdVerification) && <button onClick={() => setShowOtpModal(true)} className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-2 font-bold text-white shadow-lg shadow-blue-200 transition-colors hover:bg-blue-700" data-id="element-72" data-mp="1c5td272ndy35">
                <User className="h-4 w-4" data-id="element-73" data-mp="1qrgyrwoi1ao4" />
                {fulfillmentProfile.requiresNationalIdVerification ? 'Verify pickup identity' : 'Customer Pickup'}
              </button>}
          </div>}

        <div className="flex items-center gap-3" data-id="element-74" data-mp="1mrg9ti3rxh2t">
          {order.status === 'Delivered' && <button onClick={() => setShowReturnModal(true)} className="px-6 py-2 bg-rose-600 text-white font-bold rounded-xl shadow-lg shadow-rose-200 hover:bg-rose-700 transition-all" data-id="element-75" data-mp="1832674yj9ol7">
              Initiate Return
            </button>}

          {order.status === 'Return Requested' && <button onClick={() => onUpdateStatus('Refunded')} className="px-6 py-2 bg-slate-800 text-white font-bold rounded-xl shadow-lg shadow-slate-200 hover:bg-slate-900 transition-all" data-id="element-76" data-mp="1348spcj8xxl1">
              Complete Refund
            </button>}

          {order.status === 'Waiting Return Confirmation' && <button onClick={() => {
          // Initialize return status for items
          const initialStatus: any = {};
          items.forEach(item => {
            initialStatus[item.id] = {
              received: true,
              condition: 'Good',
              note: '',
              missingQuantity: 0
            };
          });
          setReturnItemStatus(initialStatus);
          setShowReturnReceiptModal(true);
        }} className="px-6 py-2 bg-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all" data-id="element-77" data-mp="1sebusqu6tuh2">
              Confirm Item Return
            </button>}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6" data-id="element-78" data-mp="2u4gzadnn51nj">
        <div className="col-span-2 space-y-6" data-id="element-79" data-mp="2u4qqg4kf9eut">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-80" data-mp="31nzy9tda55hr">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between" data-id="element-81" data-mp="36mjm5zbforcr">
              <div className="flex items-center gap-4" data-id="element-82" data-mp="3bd9r5bfy6s6x">
                <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-83" data-mp="3bfmfq7g7o0sy">
                  <Package className="w-4 h-4 text-blue-600" data-id="element-84" data-mp="3gbtdve2d880c" />
                  Order Items ({items.length})
                </h3>
                {order.status === 'Picking' && <button onClick={() => {
                setShowGlobalScanModal(true);
                setIsBulkScanning(true);
              }} className={cn('flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold transition-all', isBulkScanning ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')} data-id="element-85" data-mp="2noqkf0cjqngy">
                    <Scan className="w-3.5 h-3.5" data-id="element-86" data-mp="2407pfei9qtmf" />
                    {isBulkScanning ? 'Bulk Scan Active' : 'Enable Bulk Scan'}
                  </button>}
              </div>
              {order.status === 'Picking' && <div className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded" data-id="element-87" data-mp="2d1xeweslbihk">
                  {items.filter(i => i.status === 'Picked').length} /{' '}
                  {items.length} Fully Picked
                </div>}
            </div>

            <div className="divide-y divide-slate-100" data-id="element-88" data-mp="1z1e7n9vy8ggg">
              {items.map(item => <div key={item.id} className={cn('p-6 flex items-center justify-between group transition-colors', item.status === 'Picked' && 'bg-emerald-50/30', item.status === 'Unavailable' && 'bg-slate-50 opacity-70')} data-id="element-89" data-mp="1u9mhh0gpgcqu">
                  <div className="flex gap-4" data-id="element-90" data-mp="3d6puucar7sf9">
                    <div className="relative" data-id="element-91" data-mp="3hzsnmi7pixue">
                      <img src={item.image} alt={item.name} className={cn('w-16 h-16 rounded-xl object-cover border', item.status === 'Picked' ? 'border-emerald-200' : 'border-slate-200')} data-id="element-92" data-mp="3i1nai22jh014" />
                      {item.status === 'Picked' && <div className="absolute -top-2 -right-2 bg-emerald-500 text-white rounded-full p-0.5 border-2 border-white" data-id="element-93" data-mp="3mxua7d4cdfam">
                          <CheckCircle2 className="w-3.5 h-3.5" data-id="element-94" data-mp="3rzjmsx77q10t" />
                        </div>}
                    </div>
                    <div data-id="element-95" data-mp="2gitixkq9hlad">
                      <p className="font-bold text-slate-900" data-id="element-96" data-mp="2gk4nvnvg0z32">{item.name}</p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1" data-id="element-97" data-mp="387609clmqvdi">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-98" data-mp="336s2cndqt1t7">
                          SKU:{' '}
                          <span className="text-slate-600 font-mono" data-id="element-99" data-mp="2y872ip2ekk5a">
                            {item.sku}
                          </span>
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-100" data-mp="3qvksu4blihgf">
                          Barcode:{' '}
                          <span className="text-slate-600 font-mono" data-id="element-101" data-mp="3lzvozosjmeeb">
                            {item.barcode}
                          </span>
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-102" data-mp="3c76qdui3cv0e">
                          Qty:{' '}
                          <span className="text-blue-600" data-id="element-103" data-mp="8qzptwuz3dvu">
                            {item.pickedQuantity} / {item.quantity}
                          </span>
                        </p>
                        {item.isSerialized && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-bold uppercase border border-blue-100" data-id="element-104" data-mp="l2a6syppcf4l">
                            <Barcode className="w-3 h-3" data-id="element-105" data-mp="uz54xgi2cdic" />
                            Serialized
                          </div>}
                        {item.requiresPrescription && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px] font-bold uppercase border border-purple-100" data-id="element-106" data-mp="13zl1s74awme8">
                            <Info className="w-3 h-3" data-id="element-107" data-mp="1dpwiyvczws32" />
                            Rx Required
                          </div>}
                        {item.selectedLotNumber && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px] font-bold uppercase border border-amber-100" data-id="element-108" data-mp="1nnu7ljyroqw7">
                            <Info className="w-3 h-3" data-id="element-109" data-mp="2pht4sc7uusbq" />
                            Lot: {item.selectedLotNumber}
                          </div>}
                        {item.status === 'Unavailable' && <div className="rounded border border-rose-100 bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-rose-700" data-id="element-110" data-mp="2kg3u4xojogo6">Unavailable</div>}
                        {item.dispensedTradeDrugCode && item.dispensedTradeDrugCode !== item.tradeDrugCode && <div className="rounded border border-purple-100 bg-purple-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-purple-700" data-id="element-111" data-mp="2zefr75ftxrfv">Sub: {item.dispensedTradeDrugCode}</div>}
                      </div>
                      {item.serialNumbers.length > 0 && <div className="flex flex-wrap gap-1 mt-2" data-id="element-112" data-mp="3vwc2xgukko8a">
                          {item.serialNumbers.map(sn => <p key={sn} className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100" data-id="element-113" data-mp="1kfihgsrbob85">
                              SN: {sn}
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {order.status === 'Picking' && <div className="flex items-center gap-2" data-id="element-114" data-mp="wk61uuznfwkx">
                      {item.status === 'Unavailable' ? <button onClick={() => handleRestoreUnavailable(item.id)} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 transition-colors hover:border-blue-500 hover:text-blue-600" data-id="element-115" data-mp="1bhgdc4cal2gn">
                          Restore line
                        </button> : <>
                          <button onClick={() => handlePickItem(item.id)} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold transition-colors hover:border-blue-500 hover:text-blue-600" data-id="element-116" data-mp="2txz0r7ay67cr">
                            {item.isSerialized ? <Scan className="h-4 w-4" data-id="element-117" data-mp="26aqw8rlcf2bs" /> : <CheckCircle2 className="h-4 w-4" data-id="element-118" data-mp="2b80m28cn9g05" />}
                            {item.status === 'Picked' ? 'Edit' : item.isSerialized ? 'Scan Serial' : 'Confirm Qty'}
                          </button>
                          <button onClick={() => handleMarkUnavailable(item.id)} className="rounded-lg px-3 py-2 text-xs font-bold text-rose-700 transition-colors hover:bg-rose-50" data-id="element-119" data-mp="2kyc0wpxv99lt">
                            Mark unavailable
                          </button>
                        </>}
                      {fulfillmentProfile.requiresPrescriptionReview && <button onClick={() => {
                  setScanningItem(item);
                  setShowLabelModal(true);
                }} className="rounded-lg px-3 py-2 text-xs font-bold text-purple-700 transition-colors hover:bg-purple-50" data-id="element-120" data-mp="5iyteilqaotq">
                          Print label
                        </button>}
                    </div>}
                </div>)}
            </div>
          </div>

          {/* Branch Routing Flow */}
          {order.routingHistory && order.routingHistory.length > 0 && <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 shadow-sm" data-id="element-121" data-mp="38zp4hbw63s2a">
              <h3 className="text-xs font-black text-blue-400 uppercase tracking-widest mb-4 flex items-center gap-2" data-id="element-122" data-mp="344jgm7j7g3bk">
                <Globe className="w-4 h-4" data-id="element-123" data-mp="1307o1bogjm3u" />
                Branch Routing Flow
              </h3>
              <div className="flex items-center gap-4 overflow-x-auto pb-2" data-id="element-124" data-mp="17zaoeitht45h">
                <div className="flex items-center shrink-0" data-id="element-125" data-mp="181dcgxb34n7k">
                  <div className="flex flex-col items-center gap-2" data-id="element-126" data-mp="t9mpfo8sahkx">
                    <div className="w-10 h-10 rounded-full bg-white border-2 border-blue-200 flex items-center justify-center text-blue-600 shadow-sm" data-id="element-127" data-mp="y8zaq4k0bgle">
                      <MapPin className="w-5 h-5" data-id="element-128" data-mp="k8espalx1osy" />
                    </div>
                    <div className="text-center" data-id="element-129" data-mp="p9kmzcv85k4l">
                      <p className="text-xs font-bold text-slate-900" data-id="element-130" data-mp="abjw4x97dyhb">
                        Original Branch
                      </p>
                      <p className="text-[10px] text-slate-400 font-medium" data-id="element-131" data-mp="fbod0oe0wv82">
                        Innova Main
                      </p>
                    </div>
                  </div>
                </div>

                {order.routingHistory.map((route, idx) => <Fragment key={idx} data-id="element-132" data-mp="3u6f920mtkxnl" data-mpi={idx}>
                    <div className="flex flex-col items-center" data-id="element-133" data-mp="3u7gmucv7zy90">
                      <ChevronRight className="w-5 h-5 text-blue-300" data-id="element-134" data-mp="3p83zzs4cmr6f" />
                    </div>
                    <div className="flex items-center shrink-0" data-id="element-135" data-mp="7rm4af8w19aw">
                      <div className="flex flex-col items-center gap-2" data-id="element-136" data-mp="7t6why7qcg5l">
                        <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200" data-id="element-137" data-mp="2yt13vt2i90x">
                          <MapPin className="w-5 h-5" data-id="element-138" data-mp="cqgwu8wnqc87" />
                        </div>
                        <div className="text-center" data-id="element-139" data-mp="csjoexdgkdf1">
                          <p className="text-xs font-bold text-slate-900" data-id="element-140" data-mp="qond6b5kbtk4">
                            {route.branchName}
                          </p>
                          <p className="text-[10px] text-blue-500 font-bold uppercase" data-id="element-141" data-mp="2shbkavlbv156">
                            {route.timestamp.split(',')[1]}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Fragment>)}
              </div>
            </div>}

          {/* Audit Trail */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-142" data-mp="wg1eyxl86j0t">
            <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-143" data-mp="10hwebwjlimby">
              <Clock className="w-4 h-4 text-blue-600" data-id="element-144" data-mp="15gfzvvua1w3s" />
              Full Order Audit Trail
            </h3>
            <div className="space-y-6 relative before:absolute before:left-[19px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-100" data-id="element-145" data-mp="1adpppclkw9s5">
              {order.history?.map((event, idx) => <div key={event.id} className="relative pl-12" data-id="element-146" data-mp="1k4kjr352gdo5">
                  <div className="absolute left-0 top-0 w-10 h-10 rounded-full bg-white border-2 border-slate-100 flex items-center justify-center z-10" data-id="element-147" data-mp="1o4ba55qokrs5">
                    <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-148" data-mp="1y3jz1k6ovg7n" />
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-149" data-mp="1y17hh84uf7v4">
                    <div className="flex justify-between items-start" data-id="element-150" data-mp="285e84g53qlua">
                      <div data-id="element-151" data-mp="237md3d95ujlv">
                        <p className="text-sm font-bold text-slate-900" data-id="element-152" data-mp="23agxj4s6c4fa">
                          {event.status}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-153" data-mp="1u8hi2gwt58eg">
                          {event.message}
                        </p>
                        <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-154" data-mp="2qxgt23os0oac">
                          {event.actorName} · {event.actor}
                        </p>
                      </div>
                      <p className="text-[10px] font-bold text-slate-400" data-id="element-155" data-mp="2h62ovilvr0av">
                        {event.timestamp}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        <div className="space-y-6" data-id="element-156" data-mp="34w6q5jblinyp">
          {/* Order Source Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-157" data-mp="3okqszyrbo3h6">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-158" data-mp="3td0b4sm73z8w">
              <Globe className="w-4 h-4 text-blue-600" data-id="element-159" data-mp="3enujbppotbb1" />
              Order Source
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-160" data-mp="3jmfhzkpajmxd">
              <div data-id="element-161" data-mp="3jmyps8l88uxo">
                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-162" data-mp="3jkci88yc6ffg">
                  Source
                </p>
                <div className="flex items-center gap-2" data-id="element-163" data-mp="g6i7tbr4qez1">
                  {order.channel === 'Website' ? <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-md text-[10px] font-bold uppercase" data-id="element-164" data-mp="19hbeuxztvu8">
                      <Globe className="w-3 h-3" data-id="element-165" data-mp="69vnqp6mnry2" />
                      Website
                    </div> : <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50 text-orange-700 rounded-md text-[10px] font-bold uppercase" data-id="element-166" data-mp="2qxrtzhd5qjg4">
                      <Smartphone className="w-3 h-3" data-id="element-167" data-mp="18ytroxaf5vir" />
                      Aggregator
                    </div>}
                </div>
              </div>
              <div data-id="element-168" data-mp="1i1cujhe7g850">
                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-169" data-mp="1hzrvpfkuy31c">
                  Source ID
                </p>
                <p className="text-sm font-bold text-slate-900" data-id="element-170" data-mp="1dxn74v99nwlk">
                  {order.sourceOrderId || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-171" data-mp="z1ywan9g0b8p">
            <div className="flex items-center justify-between mb-4" data-id="element-172" data-mp="ubihaypz4hk8">
              <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-173" data-mp="ua7diywj39t4">
                <User className="w-4 h-4 text-blue-600" data-id="element-174" data-mp="6klfierw70mp" />
                Customer Profile
              </h3>
              {order.isLoyaltyCustomer && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px] font-black uppercase tracking-widest border border-amber-200" data-id="element-175" data-mp="bt6h7b8b8ts9">
                  Loyalty Member
                </span>}
            </div>
            <div className="space-y-4" data-id="element-176" data-mp="lqb7nn96vbev">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100" data-id="element-177" data-mp="lp9th9wvm8sx">
                <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-178" data-mp="3p8clqe591idh">
                  <User className="w-5 h-5 text-slate-400" data-id="element-179" data-mp="3kaksngsvbq7p" />
                </div>
                <div data-id="element-180" data-mp="2t06ty7ws9xb">
                  <p className="text-sm font-bold text-slate-900" data-id="element-181" data-mp="2tjpxi9ap4cx">
                    {order.customer.name}
                  </p>
                  <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider" data-id="element-182" data-mp="3u5ctcthqyt6p">
                    {order.customer.gender || 'N/A'} • {order.customer.dateOfBirth || 'DOB unavailable'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-183" data-mp="1gzy04hiaxymd">
                <div className="space-y-1" data-id="element-184" data-mp="1lr68rbbt5fyh">
                  <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-185" data-mp="1ls7t66epresv">
                    City
                  </p>
                  <p className="text-xs font-bold text-slate-700" data-id="element-186" data-mp="170gws6qivwtk">
                    {order.address.city || 'N/A'}
                  </p>
                </div>
                <div className="space-y-1" data-id="element-187" data-mp="1zs8tnkr2gk7t">
                  <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-188" data-mp="1zped44b6bjjo">
                    District
                  </p>
                  <p className="text-xs font-bold text-slate-700" data-id="element-189" data-mp="1qr2fhibw3lhq">
                    {order.address.district || 'N/A'}
                  </p>
                </div>
              </div>

              <div className="space-y-1 pt-2 border-t border-slate-50" data-id="element-190" data-mp="fbm32c7adfoy">
                <p className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1" data-id="element-191" data-mp="fdgqbx615jw7">
                  <MapPin className="w-3 h-3" data-id="element-192" data-mp="jamirlugw27q" /> Address
                </p>
                <p className="text-xs text-slate-600 leading-relaxed" data-id="element-193" data-mp="2lz34z4kpoc37">
                  {order.address.line1}, {order.address.zipCode || 'ZIP unavailable'}
                </p>
                {order.address.nationalShortAddress && <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-194" data-mp="27aq885dsup7n">
                    National address: {order.address.nationalShortAddress}
                  </p>}
              </div>

              <div className="space-y-1" data-id="element-195" data-mp="1y9sd6clc9s17">
                <p className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1" data-id="element-196" data-mp="1yc4uqon6q0dq">
                  <Phone className="w-3 h-3" data-id="element-197" data-mp="1td1pb33als58" /> Phone
                </p>
                <p className="text-xs font-bold text-blue-600" data-id="element-198" data-mp="1od5spowxd2zp">
                  {order.customer.mobile}
                </p>
                {order.customer.nationalId && <p className="text-[10px] font-bold text-slate-400" data-id="element-199" data-mp="3ipndmpuajr16">National ID: {order.customer.nationalId}</p>}
              </div>
            </div>
          </div>

          {/* Shipping & Courier Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-200" data-mp="3v6y634083p1p">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-201" data-mp="3v4sx11yyluq">
              <Truck className="w-4 h-4 text-blue-600" data-id="element-202" data-mp="8t6dxriygv8u" />
              Shipping Details
            </h3>
            <div className="space-y-4" data-id="element-203" data-mp="3893auj5r7y1p">
              <div className="space-y-2 rounded-xl border border-slate-100 bg-slate-50 p-3" data-id="element-204" data-mp="38a4ngs2f4slj">
                <div className="flex items-center justify-between" data-id="element-205" data-mp="3cbptpukg68mr">
                  <span className="text-xs font-bold uppercase text-slate-500" data-id="element-206" data-mp="3h7yfvrx2b23f">Fulfillment profile</span>
                  <span className="text-sm font-black uppercase tracking-tight text-blue-600" data-id="element-207" data-mp="3m8m465twp4ty">{fulfillmentProfile.kind}</span>
                </div>
                <p className="text-[10px] font-medium leading-4 text-slate-500" data-id="element-208" data-mp="11hlmbv7l0fb7">
                  Dispatch and escalation are determined by the order source and payer profile.
                </p>
              </div>

              {order.insurance && <div className="space-y-2 rounded-xl border border-violet-100 bg-violet-50/60 p-3" data-id="element-209" data-mp="25h0g3ldljpvq">
                  <div className="flex items-center justify-between" data-id="element-210" data-mp="2jgs2a9e9ockt">
                    <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-violet-700" data-id="element-211" data-mp="2efujbulfnei9"><ShieldCheck className="h-3 w-3" data-id="element-212" data-mp="2tccwvmh0aakj" />Insurance</span>
                    <span className="text-[10px] font-bold text-violet-600" data-id="element-213" data-mp="2oejrgvnj1klb">{order.insurance.paymentLinkStatus}</span>
                  </div>
                  <p className="text-xs font-bold text-slate-900" data-id="element-214" data-mp="324ew5hfc9kt8">{order.insurance.payerName} · {order.insurance.policyNo}</p>
                  <p className="text-[10px] text-slate-500" data-id="element-215" data-mp="2y67qbhmey3wt">Member {order.insurance.memberId} · Copay {order.insurance.copayPercent}% · Approval {order.insurance.approvalNo || 'pending'}</p>
                  <p className="text-[10px] font-bold text-violet-700" data-id="element-216" data-mp="376mdynmunv3h">Patient {order.insurance.patientShare.toFixed(2)} SAR · Insurer {order.insurance.insuranceShare.toFixed(2)} SAR</p>
                  {order.status === 'Awaiting Payment' && fulfillmentProfile.requiresPaymentLink && <div className="mt-3 space-y-3 border-t border-violet-100 pt-3" data-id="element-217" data-mp="3grvoimbszhm7">
                      <p className="text-[10px] leading-4 text-violet-800" data-id="element-218" data-mp="3lq6yqy60fu4y">Payment link sent to {order.customer.mobile}</p>
                      <div className="grid grid-cols-2 gap-2" data-id="element-219" data-mp="37ysgwunu9yzh">
                        <button onClick={() => onUpdateStatus('Picking', {
                    insurance: {
                      ...order.insurance,
                      paymentLinkStatus: 'Paid'
                    }
                  })} className="rounded-lg bg-emerald-600 px-2 py-2 text-[10px] font-black uppercase text-white transition-colors hover:bg-emerald-700" data-id="element-220" data-mp="3cr24i41hemyl">
                          Payment received
                        </button>
                        <button onClick={() => onUpdateStatus('Held', {
                    holdReason: 'Payment link expired',
                    insurance: {
                      ...order.insurance,
                      paymentLinkStatus: 'Expired'
                    }
                  })} className="rounded-lg border border-rose-200 bg-white px-2 py-2 text-[10px] font-black uppercase text-rose-700 transition-colors hover:bg-rose-50" data-id="element-221" data-mp="2t2b5y4jkamdg">
                          Link expired
                        </button>
                      </div>
                    </div>}
                </div>}

              {order.prescription && <div className="space-y-2 rounded-xl border border-purple-100 bg-purple-50/60 p-3" data-id="element-222" data-mp="1dhsg9aed0qi2">
                  <div className="flex items-center justify-between" data-id="element-223" data-mp="18mmt689815se"><span className="text-[10px] font-black uppercase tracking-wider text-purple-700" data-id="element-224" data-mp="14i5akcelkyj7">Yaser eRx</span><span className="text-[10px] font-bold text-purple-600" data-id="element-225" data-mp="3tb9ee5l7wb1h">{order.prescription.erxReference}</span></div>
                  {order.prescription.activities.map(activity => <div key={activity.genericCode} className="space-y-0.5" data-id="element-226" data-mp="24rdbc7ort6g3"><p className="text-[10px] leading-4 text-slate-600" data-id="element-227" data-mp="24syaybpxzfkt">{activity.genericCode}: {activity.instructionsAr} · {activity.durationDays} days</p><p className="text-[9px] font-medium text-purple-600" data-id="element-228" data-mp="2ds1t9dhabqy9">Allowed: {order.prescription?.acceptableTradeDrugs[activity.genericCode]?.join(', ') || 'No mapping supplied'}</p></div>)}
                </div>}

              {!fulfillmentProfile.requiresPickupOtp && <div className="space-y-4" data-id="element-229" data-mp="2nk7i4ec8ps98">
                  <div className="flex justify-between items-center" data-id="element-230" data-mp="2sg6b6f8ijyfz">
                    <span className="text-xs font-bold text-slate-400 uppercase" data-id="element-231" data-mp="2wisxk0mf0r5s">
                      Courier
                    </span>
                    <div className="flex flex-col items-end gap-1" data-id="element-232" data-mp="31gcnvywe0ue3">
                      <div className="flex items-center gap-2" data-id="element-233" data-mp="1251s7db4ctg5">
                        <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center" data-id="element-234" data-mp="126mphaotiqhp">
                          <Globe className="w-3 h-3 text-slate-500" data-id="element-235" data-mp="175g1eyogi9fr" />
                        </div>
                        <span className="text-sm font-bold text-slate-900" data-id="element-236" data-mp="38ysm8eywjhf7">
                          {order.courierName || 'Not Assigned'}
                        </span>
                      </div>
                      {order.courierPhone && <div className="flex items-center gap-1.5 text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md" data-id="element-237" data-mp="3dswv53rq9zix">
                          <Phone className="w-3 h-3" data-id="element-238" data-mp="2q4dm2m0utips" />
                          {order.courierPhone}
                        </div>}
                    </div>
                  </div>

                  {order.driverInfo && <div className="pt-4 border-t border-slate-100" data-id="element-239" data-mp="2v0uj65q177f0">
                      <p className="text-[10px] font-bold text-slate-400 uppercase mb-3" data-id="element-240" data-mp="2v3qh3u1vpw3g">
                        Assigned Driver
                      </p>
                      <div className="flex items-center gap-3" data-id="element-241" data-mp="aadcmsyuq81b">
                        <img src={order.driverInfo.avatar} alt={order.driverInfo.name} className="w-10 h-10 rounded-full border-2 border-white shadow-sm" data-id="element-242" data-mp="1s8a0qzpa1tcq" />
                        <div className="flex-1" data-id="element-243" data-mp="1ja6eg16gwsjv">
                          <p className="text-sm font-bold text-slate-900" data-id="element-244" data-mp="1o6eoj05t5vk6">
                            {order.driverInfo.name}
                          </p>
                          <p className="text-[10px] text-slate-500 font-mono" data-id="element-245" data-mp="2bz4xr1mbu0qr">
                            {order.driverInfo.vehiclePlate}
                          </p>
                        </div>
                        <button className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-246" data-mp="224v31d9wwqcv">
                          <Phone className="w-4 h-4" data-id="element-247" data-mp="2uvwqe1nrds7h" />
                        </button>
                      </div>
                    </div>}
                </div>}
            </div>
          </div>

          {/* Payment & Loyalty */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-248" data-mp="5gq0j1t6d005">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-249" data-mp="aei2konja2i2">
              <CreditCard className="w-4 h-4 text-blue-600" data-id="element-250" data-mp="f82qfnvexg3j" />
              Payment & Loyalty
            </h3>
            <div className="space-y-4" data-id="element-251" data-mp="j7v249g3s2d4">
              {/* Main Totals */}
              <div className="space-y-2" data-id="element-252" data-mp="3io9w1zxe1ney">
                <div className="flex justify-between text-sm" data-id="element-253" data-mp="3ipkxvu79wl3f">
                  <span className="text-slate-500" data-id="element-254" data-mp="3nnmf09l52ac9">Subtotal</span>
                  <span className="font-medium text-slate-900" data-id="element-255" data-mp="3rog436abhv6q">
                    {((order.totalAmount + (order.discountAmount || 0)) / 1.15).toFixed(2)}{' '}
                    SAR
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-256" data-mp="1bws8pfaycaq1">
                  <span className="text-slate-500" data-id="element-257" data-mp="1bv7fpu4aczua">VAT (15%)</span>
                  <span className="font-medium text-slate-900" data-id="element-258" data-mp="1lzxl69ywem39">
                    {((order.totalAmount + (order.discountAmount || 0)) * 0.15 / 1.15).toFixed(2)}{' '}
                    SAR
                  </span>
                </div>
              </div>

              {/* Discounts & Loyalty Breakdown */}
              {(order.discountAmount || order.loyaltyPointsUsed || order.qitafAmount || order.walletAmount) && <div className="p-3 bg-emerald-50/50 rounded-xl border border-emerald-100 space-y-2" data-id="element-259" data-mp="tc1pn8l2f082">
                  {order.discountAmount && <div className="flex justify-between text-xs" data-id="element-260" data-mp="pcarukx4fju1">
                      <div className="flex items-center gap-2" data-id="element-261" data-mp="2nqm6fa69mbm6">
                        <span className="text-emerald-700 font-bold uppercase" data-id="element-262" data-mp="2nnzyvajdjw3y">
                          Coupon
                        </span>
                        <span className="px-1.5 py-0.5 bg-white text-emerald-700 rounded text-[9px] font-black border border-emerald-200 uppercase" data-id="element-263" data-mp="h10ztxn4ac6l">
                          {order.couponCode}
                        </span>
                      </div>
                      <span className="font-bold text-emerald-700" data-id="element-264" data-mp="7zkpfpwaya2d">
                        -{order.discountAmount.toFixed(2)} SAR
                      </span>
                    </div>}
                  {order.loyaltyPointsUsed && <div className="flex justify-between text-xs" data-id="element-265" data-mp="14qlg28rvyvc6">
                      <span className="text-emerald-700 font-bold uppercase" data-id="element-266" data-mp="qs5awm9o9cw5">
                        Loyalty Points ({order.loyaltyPointsUsed})
                      </span>
                      <span className="font-bold text-emerald-700" data-id="element-267" data-mp="vst2pa5q5xrf">
                        Redeemed
                      </span>
                    </div>}
                  {order.qitafAmount && <div className="flex justify-between text-xs" data-id="element-268" data-mp="37zwxsoalaeww">
                      <span className="text-emerald-700 font-bold uppercase" data-id="element-269" data-mp="3829pvua2a5no">
                        Qitaf Points
                      </span>
                      <span className="font-bold text-emerald-700" data-id="element-270" data-mp="3hyw96emqoonl">
                        -{order.qitafAmount.toFixed(2)} SAR
                      </span>
                    </div>}
                  {order.walletAmount && <div className="flex justify-between text-xs" data-id="element-271" data-mp="2p9ysuh2391xh">
                      <span className="text-emerald-700 font-bold uppercase" data-id="element-272" data-mp="2kbnim57vspeq">
                        Wallet Balance
                      </span>
                      <span className="font-bold text-emerald-700" data-id="element-273" data-mp="2z1l7yohjrl5i">
                        -{order.walletAmount.toFixed(2)} SAR
                      </span>
                    </div>}
                </div>}

              <div className="pt-3 border-t border-slate-100 flex justify-between items-center" data-id="element-274" data-mp="21kmihdv0yntl">
                <span className="font-bold text-slate-900" data-id="element-275" data-mp="14t9as4u4n98y">Grand Total</span>
                <span className="text-lg font-black text-blue-600" data-id="element-276" data-mp="19ru55nmp6yph">
                  {order.totalAmount.toFixed(2)} SAR
                </span>
              </div>

              {/* Payment Method Selector */}
              <div className="mt-4 space-y-3" data-id="element-277" data-mp="1ijw39f9ao2wl">
                <div className={cn('p-3 rounded-xl flex items-center justify-between border', order.paymentType === 'COD' ? 'bg-amber-50 border-amber-100' : 'bg-blue-50 border-blue-100')} data-id="element-278" data-mp="1ilp8cq49ef2h">
                  <div className="flex items-center gap-2" data-id="element-279" data-mp="1xfkevpgxzsai">
                    <Info className={cn('w-4 h-4', order.paymentType === 'COD' ? 'text-amber-600' : 'text-blue-600')} data-id="element-280" data-mp="21hp1i48z3onn" />
                    <div className="flex flex-col" data-id="element-281" data-mp="3vrvv87dqqxag">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter" data-id="element-282" data-mp="3r045fz2esvld">
                        Payment Method
                      </span>
                      <span className={cn('text-xs font-black uppercase', order.paymentType === 'COD' ? 'text-amber-700' : 'text-blue-700')} data-id="element-283" data-mp="3m1aotxrtsogz">
                        {order.paymentType}
                      </span>
                    </div>
                  </div>
                  {!readOnly && !['Out for Delivery', 'Delivered', 'Delivery Attempt Failed'].includes(order.status) && <button onClick={() => setIsEditingPayment(!isEditingPayment)} className="px-3 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-black text-blue-600 hover:border-blue-500 uppercase shadow-sm transition-all" data-id="element-284" data-mp="2p9z3ok0cc2il">
                        {isEditingPayment ? 'Cancel' : 'Change'}
                      </button>}
                </div>

                {isEditingPayment && <div className="grid grid-cols-2 gap-2 p-2 bg-slate-50 rounded-xl border border-slate-100 animate-in fade-in slide-in-from-top-1" data-id="element-285" data-mp="280o3hr83k7ax">
                    {['COD', 'Visa', 'Mada', 'ApplePay', 'Tamara', 'Tabby'].map(method => <button key={method} onClick={() => {
                  onUpdatePaymentMethod?.(method as PaymentType);
                  setIsEditingPayment(false);
                }} className={cn('px-3 py-2 rounded-lg text-[10px] font-bold uppercase transition-all', order.paymentType === method ? 'bg-blue-600 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-300')} data-id="element-286" data-mp="231tnsy5feyv1">
                          {method}
                        </button>)}
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence data-id="element-287" data-mp="233r2uvsz99et">
        {showGlobalScanModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-288" data-mp="1y57dsmj37hi8">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-289" data-mp="2c60bfg4oqspk">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-290" data-mp="3nk2kswlf7yb0">
                <h3 className="font-bold text-slate-900" data-id="element-291" data-mp="3shky0z6h0zot">
                  {isBulkScanning ? 'Continuous Picking' : scanningItem ? 'Confirm Quantity' : 'Scan Item'}
                </h3>
                <button onClick={() => {
              setShowGlobalScanModal(false);
              setIsBulkScanning(false);
              setScanningItem(null);
            }} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-292" data-mp="34rqlkiq2dd53">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-293" data-mp="2h25rp4sdgvh1" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-294" data-mp="29idainnxjoro">
                {scanningItem ? <div className="space-y-6" data-id="element-295" data-mp="24kbnjrndv5ax">
                    <div className="flex flex-col items-center text-center space-y-3" data-id="element-296" data-mp="24iifq911af1c">
                      <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600" data-id="element-297" data-mp="1zjp4yod4t24v">
                        <Package className="w-8 h-8" data-id="element-298" data-mp="1vm9fwt959eeo" />
                      </div>
                      <div data-id="element-299" data-mp="1qmwwyjle8rhd">
                        <h4 className="font-bold text-xl text-slate-900" data-id="element-300" data-mp="1ludjrnqmbyd3">
                          {scanningItem.name}
                        </h4>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-301" data-mp="1gvk4pqvonzat">
                          SKU: {scanningItem.sku}
                        </p>
                      </div>
                      <div className="flex items-center gap-8 pt-2" data-id="element-302" data-mp="17tt1qtsj26kn">
                        <div className="text-center" data-id="element-303" data-mp="12uzmavtok5hu">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-304" data-mp="12xlriot3m8ww">
                            Ordered
                          </p>
                          <p className="text-lg font-black text-slate-900" data-id="element-305" data-mp="3jw3odvfzccao">
                            {scanningItem.quantity}
                          </p>
                        </div>
                        <div className="text-center" data-id="element-306" data-mp="39wml1ko7aahv">
                          <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest" data-id="element-307" data-mp="34ybf3l110k4x">
                            Picked
                          </p>
                          <p className="text-lg font-black text-emerald-600" data-id="element-308" data-mp="305i6gm2fsrnk">
                            {scanningItem.pickedQuantity}
                          </p>
                        </div>
                        <div className="text-center" data-id="element-309" data-mp="2r8rbye3j9kv3">
                          <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest" data-id="element-310" data-mp="2m83f3bsmi9sy">
                            Remaining
                          </p>
                          <p className="text-lg font-black text-blue-600" data-id="element-311" data-mp="274tt5b26s3z0">
                            {scanningItem.quantity - scanningItem.pickedQuantity}
                          </p>
                        </div>
                      </div>
                    </div>

                    {scanningItem.lotNumbers && <div className="space-y-3" data-id="element-312" data-mp="2m1klx1hcuhfn">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-313" data-mp="1oekj5f05r7wr">
                          Select Lot Number
                        </label>
                        <div className="grid gap-2" data-id="element-314" data-mp="1tb2j81kjmi2r">
                          {scanningItem.lotNumbers.map(lot => {
                    const therapyEnd = fulfillmentProfile.requiresPrescriptionReview ? getTherapyEndDate(scanningItem.genericCode) : null;
                    const expiresBeforeTherapyEnds = therapyEnd ? new Date(`${lot.expiryDate}-01`).getTime() < therapyEnd.getTime() : false;
                    return <button key={lot.number} onClick={() => setTempLot(lot.number)} className={cn('flex items-center justify-between rounded-2xl border-2 p-4 text-left transition-colors', tempLot === lot.number ? 'border-blue-600 bg-blue-50/50' : expiresBeforeTherapyEnds ? 'border-amber-200 bg-amber-50/50 hover:border-amber-400' : 'border-slate-100 hover:border-slate-200')} data-id="element-315" data-mp="xmv6hu94yo2u">
                                <div data-id="element-316" data-mp="200kbnr3jwezn">
                                  <p className="text-sm font-black text-slate-900" data-id="element-317" data-mp="203q1j8k07kwf">{lot.number}</p>
                                  <p className="text-[10px] font-bold uppercase text-slate-400" data-id="element-318" data-mp="27n7oyx0dcuy9">EXP: {lot.expiryDate}</p>
                                  {expiresBeforeTherapyEnds && <p className="mt-1 text-[10px] font-bold text-amber-700" data-id="element-319" data-mp="1yl8762hj5mu9">Expires before end of therapy</p>}
                                </div>
                                <div className="text-right" data-id="element-320" data-mp="22nv1qb5l5lv5"><p className="text-[10px] font-black uppercase tracking-widest text-slate-300" data-id="element-321" data-mp="2qb4adx24wieh">Stock</p><p className="text-sm font-black text-slate-600" data-id="element-322" data-mp="2vaftvdtvy1zr">{lot.stock}</p></div>
                              </button>;
                  })}
                        </div>
                      </div>}

                    {fulfillmentProfile.requiresPrescriptionReview && scanningItem.allowSubstitution && <div className="space-y-3" data-id="element-323" data-mp="398njt1si831f">
                        <label className="text-[10px] font-black uppercase tracking-widest text-purple-600" data-id="element-324" data-mp="3e28kj1lhfb4b">Approved trade drug</label>
                        <div className="grid gap-2" data-id="element-325" data-mp="1qasmuleujw9f">
                          {(order.prescription?.acceptableTradeDrugs[scanningItem.genericCode] || [scanningItem.tradeDrugCode]).map(tradeDrugCode => <button key={tradeDrugCode} onClick={() => setTempSubstitution(tradeDrugCode)} className={cn('rounded-xl border-2 px-4 py-3 text-left text-sm font-bold transition-colors', (tempSubstitution || scanningItem.tradeDrugCode) === tradeDrugCode ? 'border-purple-600 bg-purple-50 text-purple-800' : 'border-slate-100 bg-white text-slate-600 hover:border-purple-200')} data-id="element-326" data-mp="1ggiuv4ttgzz8">
                              {tradeDrugCode}{tradeDrugCode === scanningItem.tradeDrugCode ? ' · prescribed' : ' · approved substitute'}
                            </button>)}
                        </div>
                      </div>}

                    <div className="space-y-3" data-id="element-327" data-mp="9ml33eqrh0la">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-328" data-mp="eitcscm6w1l2">
                        Confirm Picked Quantity
                      </label>
                      <div className="flex items-center gap-4" data-id="element-329" data-mp="nk1cmz4ctah9">
                        <button onClick={() => setManualQty(Math.max(0, parseInt(manualQty || '0') - 1).toString())} className="w-14 h-14 rounded-2xl border-2 border-slate-100 flex items-center justify-center text-2xl font-black text-slate-400 hover:bg-slate-50 transition-colors" data-id="element-330" data-mp="ni6m95abcq5s">
                          -
                        </button>
                        <div className="flex-1 relative" data-id="element-331" data-mp="3cbtoy98awe4b">
                          <input type="number" value={manualQty} onChange={e => setManualQty(e.target.value)} className="w-full text-center py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none font-black text-2xl text-slate-900 focus:border-blue-500 transition-all" data-id="element-332" data-mp="3cccx4y85fo55" />
                        </div>
                        <button onClick={() => setManualQty(Math.min(scanningItem.quantity, parseInt(manualQty || '0') + 1).toString())} className="w-14 h-14 rounded-2xl border-2 border-slate-100 flex items-center justify-center text-2xl font-black text-slate-400 hover:bg-slate-50 transition-colors" data-id="element-333" data-mp="3r1r82ardidmn">
                          +
                        </button>
                      </div>
                      {parseInt(manualQty) < scanningItem.quantity && <p className="text-[10px] font-bold text-amber-600 flex items-center gap-1.5" data-id="element-334" data-mp="bdq1jx0tkqaj">
                          <AlertCircle className="w-3 h-3" data-id="element-335" data-mp="gcj5axqb12xi" />
                          Partial fulfillment:{' '}
                          {scanningItem.quantity - parseInt(manualQty)} units
                          pending
                        </p>}
                    </div>

                    <div className="flex gap-4 pt-4" data-id="element-336" data-mp="3oop2t7cthae3">
                      <button onClick={() => {
                  setScanningItem(null);
                  setTempLot('');
                  setManualQty('');
                }} className="flex-1 py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-337" data-mp="3tmgxg94ujalz">
                        Back to Scan
                      </button>
                      <button disabled={(scanningItem.requiresLot || scanningItem.lotNumbers && scanningItem.lotNumbers.length > 0) && !tempLot} onClick={() => {
                  handleConfirmItemFulfillment();
                }} className="flex-[1.5] py-4 bg-blue-400 text-white font-black rounded-2xl shadow-lg shadow-blue-100 hover:bg-blue-500 transition-all disabled:opacity-50" data-id="element-338" data-mp="anyqj5b4xg1l">
                        Confirm & Next Scan
                      </button>
                    </div>
                  </div> : <div className="space-y-8" data-id="element-339" data-mp="1cbzsadifdu9d">
                    <div className="text-center space-y-2" data-id="element-340" data-mp="1c8vjf2u0macd">
                      <div className="w-20 h-20 bg-blue-50 rounded-3xl flex items-center justify-center mx-auto text-blue-600" data-id="element-341" data-mp="1jtpk8ctjhbv2">
                        <Scan className="w-10 h-10" data-id="element-342" data-mp="1orhaz3ig6rxo" />
                      </div>
                      <h4 className="font-bold text-xl text-slate-900" data-id="element-343" data-mp="1tksmhhy7fwvx">
                        Ready to Scan
                      </h4>
                      <p className="text-sm text-slate-500" data-id="element-344" data-mp="10vmprii82mpv">
                        Scan an item's barcode or serial number to begin
                        fulfillment.
                      </p>
                    </div>

                    <div className="space-y-2" data-id="element-345" data-mp="1at9ib7sg2csb">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-346" data-mp="1ashnw0al2l3m">
                        Scan Item
                      </label>
                      <div className="relative" data-id="element-347" data-mp="2m7wkv42slcyq">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2" data-id="element-348" data-mp="2mbajc18c520w">
                          <Scan className="w-5 h-5 text-blue-400" data-id="element-349" data-mp="2ra5fs5z86p3h" />
                        </div>
                        <input autoFocus type="text" value={globalScanValue} onChange={e => setGlobalScanValue(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleGlobalScan(globalScanValue);
                    }
                  }} placeholder="Scan barcode, SKU, or Lot..." className="w-full pl-12 pr-4 py-5 bg-white border-2 border-blue-500 rounded-2xl outline-none shadow-[0_0_0_4px_rgba(59,130,246,0.1)] font-mono font-bold text-slate-700 placeholder:text-slate-300 transition-all" data-id="element-350" data-mp="3fxxevvo4n51m" />
                      </div>
                    </div>

                    <div className="flex gap-4 pt-4" data-id="element-351" data-mp="qfex90bimkzh">
                      <button onClick={() => {
                  setShowGlobalScanModal(false);
                  setIsBulkScanning(false);
                }} className="w-full py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-352" data-mp="qe3pyqiv2v3m">
                        Cancel & Close
                      </button>
                    </div>
                  </div>}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Cancel Order Modal */}
      <AnimatePresence data-id="element-353" data-mp="2pny4kj9m1tmc">
        {showRejectModal && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-354" data-mp="33kjxrgyj6uaf">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-355" data-mp="2yn1kxfhe7ux5">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-rose-50/60" data-id="element-356" data-mp="3e84y3b49fsz1">
                <div className="flex items-center gap-3" data-id="element-357" data-mp="3ic2sj8in4flr">
                  <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center" data-id="element-358" data-mp="3na4atr88saw6">
                    <XCircle className="w-6 h-6" data-id="element-359" data-mp="3s7ntd3bz39tx" />
                  </div>
                  <div data-id="element-360" data-mp="ti06sriu6cj">
                    <h3 className="font-bold text-slate-900" data-id="element-361" data-mp="v2vwlpknvbz">Cancel Order</h3>
                    <p className="text-[10px] font-bold text-rose-600 uppercase tracking-widest" data-id="element-362" data-mp="5tmhgl09753t">
                      Reason required
                    </p>
                  </div>
                </div>
                <button onClick={() => {
              setShowRejectModal(false);
              setRejectionReason('');
              setCustomReason('');
            }} className="p-1 rounded-full hover:bg-white transition-colors" aria-label="Close cancellation dialog" data-id="element-363" data-mp="et17xkgf7fob">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-364" data-mp="1qd00vc4yo9y3" />
                </button>
              </div>

              <div className="p-6 space-y-4" data-id="element-365" data-mp="2i2bism0sotmz">
                <p className="text-sm text-slate-600" data-id="element-366" data-mp="2i11kpwtxzlgc">
                  Select why this order is being cancelled. This will be saved
                  in the order history.
                </p>
                <div className="space-y-2" data-id="element-367" data-mp="298zo69mzupbc">
                  {cancelReasons.map(reason => <button key={reason.id} onClick={() => {
                setRejectionReason(reason.label);
                if (!reason.requiresFreeText) setCustomReason('');
              }} className={cn('w-full px-4 py-3 rounded-xl border-2 text-left text-sm font-bold transition-all', rejectionReason === reason.label ? 'border-rose-600 bg-rose-50 text-rose-700' : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200')} data-id="element-368" data-mp="249vab5p4k3eo">
                      {reason.label}
                    </button>)}
                </div>

                {cancelReasonRequiresText && <textarea value={customReason} onChange={event => setCustomReason(event.target.value)} placeholder="Enter the cancellation reason..." className="w-full min-h-[88px] p-4 rounded-xl border-2 border-slate-100 bg-slate-50 text-sm outline-none focus:border-rose-500 focus:bg-white transition-colors" data-id="element-369" data-mp="34brse6yq88hl" />}
              </div>

              <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3" data-id="element-370" data-mp="16aj7xsyny15">
                <button onClick={() => {
              setShowRejectModal(false);
              setRejectionReason('');
              setCustomReason('');
            }} className="flex-1 py-3.5 rounded-xl text-slate-600 font-bold hover:bg-white transition-colors" data-id="element-371" data-mp="3tbehdw6lm4iy">
                  Go Back
                </button>
                <button disabled={!rejectionReason || cancelReasonRequiresText && !customReason.trim()} onClick={() => {
              const reason = cancelReasonRequiresText ? customReason.trim() : rejectionReason;
              onUpdateStatus('Cancelled', {
                rejectionReason: reason
              });
              setShowRejectModal(false);
              setRejectionReason('');
              setCustomReason('');
            }} className="flex-1 py-3.5 rounded-xl bg-rose-600 text-white font-bold shadow-lg shadow-rose-100 hover:bg-rose-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-372" data-mp="3aca9iqqsmgsv">
                  Confirm Cancel
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Reject Pending Order Modal */}
      <AnimatePresence data-id="element-373" data-mp="rxzwnkrt4lw4">
        {showOrderRejectionModal && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-id="element-374" data-mp="ryj76gf4o804">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="reject-order-title" data-id="element-375" data-mp="wvjd8rbz9ar8">
              <div className="flex items-center justify-between border-b border-amber-100 bg-amber-50/70 p-6" data-id="element-376" data-mp="3sutjn14yqpg3">
                <div className="flex items-center gap-3" data-id="element-377" data-mp="3nuxq5vtw6ges">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700" data-id="element-378" data-mp="3nxjyhxolwzy2">
                    <AlertCircle className="h-6 w-6" data-id="element-379" data-mp="3eushzjgvjdzk" />
                  </div>
                  <div data-id="element-380" data-mp="3ewviixfnkpij">
                    <h3 id="reject-order-title" className="font-bold text-slate-900" data-id="element-381" data-mp="3a42bg2wpp53a">Reject Order</h3>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700" data-id="element-382" data-mp="354pt9vgscm71">Return to system routing</p>
                  </div>
                </div>
                <button onClick={() => {
              setShowOrderRejectionModal(false);
              setOrderRejectionReason('');
              setOrderRejectionCustomReason('');
            }} className="rounded-full p-1 text-slate-400 transition-colors hover:bg-white" aria-label="Close order rejection dialog" data-id="element-383" data-mp="303s0koy5g9ra">
                  <XCircle className="h-5 w-5" data-id="element-384" data-mp="nt8qv16t75el" />
                </button>
              </div>

              <div className="space-y-4 p-6" data-id="element-385" data-mp="90excbupcoqx">
                <p className="text-sm leading-6 text-slate-600" data-id="element-386" data-mp="dwm4u92qy8az">
                  Choose why this pending order cannot be accepted. The routing system will receive it for reassignment.
                </p>
                <div className="space-y-2" data-id="element-387" data-mp="4xscww0d263r">
                  {rejectReasons.map(reason => <button key={reason.id} onClick={() => {
                setOrderRejectionReason(reason.label);
                if (!reason.requiresFreeText) setOrderRejectionCustomReason('');
              }} className={cn('w-full rounded-xl border-2 px-4 py-3 text-left text-sm font-bold transition-colors', orderRejectionReason === reason.label ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200')} data-id="element-388" data-mp="3me9xbcqdg1jz">
                      {reason.label}
                    </button>)}
                </div>
                {rejectReasonRequiresText && <textarea value={orderRejectionCustomReason} onChange={event => setOrderRejectionCustomReason(event.target.value)} placeholder="Enter the routing reason..." className="min-h-[88px] w-full rounded-xl border-2 border-slate-100 bg-slate-50 p-4 text-sm outline-none transition-colors focus:border-amber-500 focus:bg-white" data-id="element-389" data-mp="3cpmgvvtjpnp2" />}
              </div>

              <div className="flex gap-3 border-t border-slate-100 bg-slate-50 p-6" data-id="element-390" data-mp="3vdzusnexxzvd">
                <button onClick={() => {
              setShowOrderRejectionModal(false);
              setOrderRejectionReason('');
              setOrderRejectionCustomReason('');
            }} className="flex-1 rounded-xl py-3.5 font-bold text-slate-600 transition-colors hover:bg-white" data-id="element-391" data-mp="sljgq0kvptas">
                  Go Back
                </button>
                <button disabled={!orderRejectionReason || rejectReasonRequiresText && !orderRejectionCustomReason.trim()} onClick={() => {
              const reason = rejectReasonRequiresText ? orderRejectionCustomReason.trim() : orderRejectionReason;
              onUpdateStatus('Rejected', {
                rejectionReason: reason
              });
              setShowOrderRejectionModal(false);
              setOrderRejectionReason('');
              setOrderRejectionCustomReason('');
            }} className="flex-1 rounded-xl bg-amber-600 py-3.5 font-bold text-white shadow-lg shadow-amber-100 transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-392" data-mp="1bi2l6ckqmt10">
                  Return to Routing
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Aggregator Unavailable Rejection Modal */}
      <AnimatePresence data-id="element-393" data-mp="1q59mrroc2en8">
        {showAggregatorRejectionModal && <div className="fixed inset-0 z-[65] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-id="element-394" data-mp="1uxub4dogzzjd">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="aggregator-rejection-title" data-id="element-395" data-mp="1g9zggrqwjl3n">
              <div className="flex items-center gap-3 border-b border-rose-100 bg-rose-50/70 p-6" data-id="element-396" data-mp="1c6jpoc1ikeyv"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rose-100 text-rose-600" data-id="element-397" data-mp="3dxabih34wfrp"><XCircle className="h-6 w-6" data-id="element-398" data-mp="394h8pyr87hi9" /></div><div data-id="element-399" data-mp="393fkstp430j4"><h3 id="aggregator-rejection-title" className="font-bold text-slate-900" data-id="element-400" data-mp="393fmqz8o9alr">Reject order — item unavailable</h3><p className="text-[10px] font-bold uppercase tracking-widest text-rose-600" data-id="element-401" data-mp="3442xk7ubvrg0">Partner partial fulfillment is not allowed</p></div></div>
              <div className="space-y-4 p-7" data-id="element-402" data-mp="ontrocjme3b"><p className="text-sm leading-6 text-slate-600" data-id="element-403" data-mp="oxil8luqf7f">This aggregator order contains a short or unavailable line. It must be returned to routing rather than partially fulfilled.</p><div className="rounded-xl border border-rose-100 bg-rose-50 p-4 text-sm font-bold text-rose-800" data-id="element-404" data-mp="3nt3mvx54a37w">Reason: Item unavailable</div></div>
              <div className="flex gap-3 border-t border-slate-100 bg-slate-50 p-6" data-id="element-405" data-mp="2cf9meois94da"><button onClick={() => setShowAggregatorRejectionModal(false)} className="flex-1 rounded-xl py-3.5 font-bold text-slate-600 transition-colors hover:bg-white" data-id="element-406" data-mp="2cgkqkpg54e4x">Go Back</button><button onClick={() => {
              onUpdateStatus('Rejected', {
                rejectionReason: 'Item unavailable'
              });
              setShowAggregatorRejectionModal(false);
            }} className="flex-1 rounded-xl bg-rose-600 py-3.5 font-bold text-white shadow-lg shadow-rose-100 transition-colors hover:bg-rose-700" data-id="element-407" data-mp="mz7015r705s3">Reject order</button></div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Partial Fulfillment Modal */}
      <AnimatePresence data-id="element-408" data-mp="15o3ub9mlayab">
        {showPartialModal && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-409" data-mp="1an70ixeb3ajv">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-[32px] w-full max-w-lg overflow-hidden shadow-2xl" data-id="element-410" data-mp="1fnut3ni6nzg7">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-411" data-mp="2q3ryl9m5hqdf">
                <h3 className="font-bold text-slate-900" data-id="element-412" data-mp="2m3gj0b8nag8t">
                  Partial Fulfillment
                </h3>
                <button onClick={() => setShowPartialModal(false)} className="p-1 hover:bg-slate-100 rounded-full transition-colors" data-id="element-413" data-mp="301oh4mhf3nlk">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-414" data-mp="27fd7kmbu2ccm" />
                </button>
              </div>

              <div className="p-8 space-y-8" data-id="element-415" data-mp="22dxogzt7ufwv">
                <div className="text-center space-y-4" data-id="element-416" data-mp="2h56j5nbz6rgv">
                  <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto text-amber-500" data-id="element-417" data-mp="2h6q0zida5akr">
                    <AlertCircle className="w-8 h-8" data-id="element-418" data-mp="1oo2k6f5bghph" />
                  </div>
                  <div className="space-y-2" data-id="element-419" data-mp="1om9f34acq5jl">
                    <h4 className="text-xl font-black text-slate-900" data-id="element-420" data-mp="1johnkbdmclfx">
                      Items Still Pending
                    </h4>
                    <p className="text-sm text-slate-500 font-medium max-w-[280px] mx-auto" data-id="element-421" data-mp="3vartm5kfxwho">
                      Some items in this order have not been fully scanned or
                      picked.
                    </p>
                  </div>
                </div>

                <div className="space-y-3 max-h-[240px] overflow-auto pr-2 custom-scrollbar" data-id="element-422" data-mp="3qeky76po83dz">
                  {items.filter(i => i.status !== 'Picked' && i.status !== 'Unavailable').map(item => <div key={item.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between" data-id="element-423" data-mp="8x0dxskd0v5p">
                        <div data-id="element-424" data-mp="2tplkvda8cbex">
                          <p className="text-sm font-black text-slate-900" data-id="element-425" data-mp="2yn50aginyu8g">
                            {item.sku}
                          </p>
                          <p className="text-[10px] font-bold text-slate-400" data-id="element-426" data-mp="145vlc5xi5316">
                            {item.name}
                          </p>
                        </div>
                        <div className="text-right" data-id="element-427" data-mp="ufirfn3srxid">
                          <p className="text-sm font-black text-amber-600" data-id="element-428" data-mp="qeyt4g7hmnun">
                            {item.pickedQuantity} / {item.quantity}
                          </p>
                          <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest" data-id="element-429" data-mp="lh72rqmhr9sk">
                            {item.status}
                          </p>
                        </div>
                      </div>)}
                </div>

                <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100" data-id="element-430" data-mp="7hfrvyr9u9is">
                  <p className="text-[11px] text-amber-700 font-bold leading-relaxed" data-id="element-431" data-mp="25upjbl64kocu">
                    <span className="font-black uppercase tracking-wider mr-1" data-id="element-432" data-mp="21tvvsswlhbkh">
                      Note:
                    </span>
                    Proceeding will mark the order as partially fulfilled. This
                    will trigger a notification to the customer and the ordering
                    channel.
                  </p>
                </div>

                <div className="flex gap-4" data-id="element-433" data-mp="3tjy0ld7tnrvs">
                  <button onClick={() => setShowPartialModal(false)} className="flex-1 py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-434" data-mp="1cq0v3jrcygv">
                    Go Back
                  </button>
                  <button type="button" onClick={handleConfirmPartial} className="flex-1 py-4 bg-amber-500 text-white font-black rounded-2xl shadow-lg shadow-amber-100 hover:bg-amber-600 transition-all" data-id="element-435" data-mp="39t2wysg9om8s">
                    Confirm Partial
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Customer Confirmation Wait Modal */}
      <AnimatePresence data-id="element-436" data-mp="rl3ye27dgrj">
        {isWaitingConfirmation && <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" role="alertdialog" aria-modal="true" aria-labelledby="customer-confirmation-title" aria-describedby="customer-confirmation-description" data-id="element-437" data-mp="oyyql2sbdch">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="w-full max-w-sm overflow-hidden rounded-[28px] bg-white shadow-2xl" data-id="element-438" data-mp="jht1mgn4vf0c">
              <div className="p-8 text-center" data-id="element-439" data-mp="2l35eo6fcqyxv">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50 text-amber-600" data-id="element-440" data-mp="2l3f67yg1pe5o">
                  <Clock className="h-8 w-8" data-id="element-441" data-mp="2p6bjd9mtgi28" />
                </div>
                <h3 id="customer-confirmation-title" className="mt-5 text-xl font-black text-slate-900" data-id="element-442" data-mp="2bcklwzmd0omv">
                  Waiting for Customer Confirmation
                </h3>
                <p id="customer-confirmation-description" className="mt-2 text-sm leading-6 text-slate-500" data-id="element-443" data-mp="21h9428fnn1aw">
                  The partial fulfillment request has been sent. Choose the customer’s response to continue.
                </p>
                <div className="mt-6 grid grid-cols-2 gap-3 text-left" data-id="element-444" data-mp="1xff9mhw1ytj6">
                  <button onClick={() => resolveCustomerPartialConfirmation(true)} className="rounded-xl bg-emerald-600 px-3 py-3 text-center text-sm font-bold text-white transition-colors hover:bg-emerald-700" data-id="element-445" data-mp="3mssgoevjuvnz">
                    Customer confirmed
                  </button>
                  <button onClick={() => resolveCustomerPartialConfirmation(false)} className="rounded-xl border border-rose-200 bg-white px-3 py-3 text-center text-sm font-bold text-rose-700 transition-colors hover:bg-rose-50" data-id="element-446" data-mp="12acjr2xvctjh">
                    Customer declined
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Return Receipt Confirmation Modal */}
      <AnimatePresence data-id="element-447" data-mp="1v12vtkrlhg2t">
        {showReturnReceiptModal && <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-448" data-mp="1v0kyslx3yxtu">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-[32px] w-full max-w-2xl overflow-hidden shadow-2xl" data-id="element-449" data-mp="3rvz7503lghee">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-emerald-50/50" data-id="element-450" data-mp="206q575o7g6fy">
                <div className="flex items-center gap-3" data-id="element-451" data-mp="2wwqpardmpyqx">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600" data-id="element-452" data-mp="2wu4iitykbn9r">
                    <CheckCircle2 className="w-6 h-6" data-id="element-453" data-mp="2nv9dvmb62l8k" />
                  </div>
                  <div data-id="element-454" data-mp="2iwoetq7ni7lp">
                    <h3 className="font-bold text-slate-900" data-id="element-455" data-mp="2ix7nshfbplnl">
                      Confirm Return Receipt
                    </h3>
                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest" data-id="element-456" data-mp="1ci6z8wsmo5r1">
                      Verify Items from Driver
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowReturnReceiptModal(false)} className="p-1 hover:bg-slate-100 rounded-full transition-colors" data-id="element-457" data-mp="18zhns9cirep6">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-458" data-mp="zxqi34hzb7vb" />
                </button>
              </div>

              <div className="p-8 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar" data-id="element-459" data-mp="14z7gmtczqi8q">
                <div className="space-y-4" data-id="element-460" data-mp="q6vtvul69y78">
                  {items.map(item => <div key={item.id} className={cn('p-5 rounded-2xl border-2 transition-all space-y-4', returnItemStatus[item.id]?.condition === 'Good' ? 'border-slate-100 bg-slate-50/30' : returnItemStatus[item.id]?.condition === 'Damaged' ? 'border-rose-100 bg-rose-50/30' : 'border-amber-100 bg-amber-50/30')} data-id="element-461" data-mp="v0hvmrpssho9">
                      <div className="flex items-center justify-between" data-id="element-462" data-mp="3jtqazc4nk0mj">
                        <div className="flex items-center gap-4" data-id="element-463" data-mp="2ktlb16pkygn">
                          <img src={item.image} className="w-12 h-12 rounded-xl object-cover border border-slate-200" alt="" data-id="element-464" data-mp="2m4ph242g88a" />
                          <div data-id="element-465" data-mp="6mojhwtcevq7">
                            <p className="text-sm font-black text-slate-900" data-id="element-466" data-mp="6ldj86z3w63u">
                              {item.name}
                            </p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-467" data-mp="ld5o1p19y1rb">
                              SKU: {item.sku} • Qty: {item.quantity}
                            </p>
                          </div>
                        </div>
                        <div className="flex bg-white rounded-xl p-1 border border-slate-200 shadow-sm" data-id="element-468" data-mp="qbz0rf8qloqf">
                          {(['Good', 'Damaged', 'Missing'] as const).map(cond => <button key={cond} onClick={() => setReturnItemStatus(prev => ({
                      ...prev,
                      [item.id]: {
                        ...prev[item.id],
                        condition: cond,
                        received: cond !== 'Missing'
                      }
                    }))} className={cn('px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all', returnItemStatus[item.id]?.condition === cond ? cond === 'Good' ? 'bg-emerald-500 text-white shadow-md shadow-emerald-100' : cond === 'Damaged' ? 'bg-rose-500 text-white shadow-md shadow-rose-100' : 'bg-amber-500 text-white shadow-md shadow-amber-100' : 'text-slate-400 hover:bg-slate-50')} data-id="element-469" data-mp="2opv2aw5yn0bk">
                                {cond}
                              </button>)}
                        </div>
                      </div>

                      {returnItemStatus[item.id]?.condition !== 'Good' && <div className="animate-in fade-in slide-in-from-top-2 duration-200 space-y-4" data-id="element-470" data-mp="2h8f0qljszrw3">
                          {returnItemStatus[item.id]?.condition === 'Missing' && <div className="space-y-2" data-id="element-471" data-mp="2vdyuhvvrsuyh">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-472" data-mp="2vce1wbt0nm39">
                                How many units are missing?
                              </label>
                              <div className="flex items-center gap-3" data-id="element-473" data-mp="2rcn43o52o5p8">
                                <button onClick={() => setReturnItemStatus(prev => ({
                        ...prev,
                        [item.id]: {
                          ...prev[item.id],
                          missingQuantity: Math.max(0, prev[item.id].missingQuantity - 1)
                        }
                      }))} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-lg font-bold text-slate-400 hover:bg-slate-50" data-id="element-474" data-mp="2rcvk3owhrr4n">
                                  -
                                </button>
                                <div className="w-16 text-center font-black text-lg text-slate-900" data-id="element-475" data-mp="1v5im72jwe4nn">
                                  {returnItemStatus[item.id]?.missingQuantity || 0}
                                </div>
                                <button onClick={() => setReturnItemStatus(prev => ({
                        ...prev,
                        [item.id]: {
                          ...prev[item.id],
                          missingQuantity: Math.min(item.quantity, prev[item.id].missingQuantity + 1)
                        }
                      }))} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-lg font-bold text-slate-400 hover:bg-slate-50" data-id="element-476" data-mp="13crsoztlmu23">
                                  +
                                </button>
                                <span className="text-xs text-slate-400 font-bold" data-id="element-477" data-mp="m46nee80f5tp">
                                  / {item.quantity} units total
                                </span>
                              </div>
                            </div>}
                          <textarea placeholder={`Describe the ${returnItemStatus[item.id]?.condition.toLowerCase()} issue...`} value={returnItemStatus[item.id]?.note} onChange={e => setReturnItemStatus(prev => ({
                    ...prev,
                    [item.id]: {
                      ...prev[item.id],
                      note: e.target.value
                    }
                  }))} className="w-full p-3 bg-white border border-slate-200 rounded-xl text-xs outline-none focus:border-blue-500 transition-all min-h-[60px]" data-id="element-478" data-mp="10v6vgi4ugxpb" />
                        </div>}
                    </div>)}
                </div>
              </div>

              <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-4" data-id="element-479" data-mp="2uba4g8totdsn">
                <button onClick={() => setShowReturnReceiptModal(false)} className="flex-1 py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-480" data-mp="3rtthgp43wtsx">
                  Cancel
                </button>
                <button onClick={() => {
              onUpdateStatus('Return Requested');
              setShowReturnReceiptModal(false);
              import('sonner').then(({
                toast
              }) => toast.success('Return receipt confirmed. Status updated to Return Requested.'));
            }} className="flex-[2] py-4 bg-emerald-600 text-white font-black rounded-2xl shadow-lg shadow-emerald-100 hover:bg-emerald-700 transition-all" data-id="element-481" data-mp="2szcd5ak3cp4i">
                  Confirm Receipt & Proceed
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Return Modal */}
      <AnimatePresence data-id="element-482" data-mp="3vkmoc9c0wvqy">
        {showReturnModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-483" data-mp="3vj1pi7ioeqna">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl" data-id="element-484" data-mp="1ez97sf6gcm9u">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-rose-50/50" data-id="element-485" data-mp="1xp7fgu6c8dcy">
                <div className="flex items-center gap-3" data-id="element-486" data-mp="1sxpew7888esi">
                  <ArrowLeft className="w-6 h-6 text-rose-600" data-id="element-487" data-mp="1nznu9hv5k7ex" />
                  <h3 className="font-bold text-slate-900" data-id="element-488" data-mp="1nze3hs2a9w86">Return Process</h3>
                </div>
                <button onClick={() => setShowReturnModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-489" data-mp="2lgcsz2ot2tk3">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-490" data-mp="erubptnc0oib" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-491" data-mp="jokrefv85i37">
                <div className="grid grid-cols-2 gap-6" data-id="element-492" data-mp="jrp6i8a89y8n">
                  <div className="space-y-2" data-id="element-493" data-mp="506ns2pzhpll">
                    <label className="text-xs font-bold text-slate-500 uppercase" data-id="element-494" data-mp="a02uwatz9x5d">
                      Return Reason
                    </label>
                    <select value={returnReason} onChange={event => {
                  setReturnReason(event.target.value);
                  if (!returnReasons.find(reason => reason.label === event.target.value)?.requiresFreeText) setReturnCustomReason('');
                }} className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl text-sm outline-none" data-id="element-495" data-mp="3s4weol9ugq6f">
                      <option value="" data-id="element-496" data-mp="1le6nbckx3d8b">Select a reason...</option>
                      {returnReasons.map(reason => <option key={reason.id} value={reason.label} data-id="element-497" data-mp="35ybsynxc13g7">{reason.label}</option>)}
                    </select>
                    {returnReasonRequiresText && <textarea value={returnCustomReason} onChange={event => setReturnCustomReason(event.target.value)} placeholder="Enter the return reason..." className="min-h-[76px] w-full rounded-xl border-2 border-slate-100 bg-slate-50 p-3 text-sm outline-none transition-colors focus:border-rose-500 focus:bg-white" data-id="element-498" data-mp="3fmtpuu2hqqek" />}
                  </div>
                  <div className="space-y-2" data-id="element-499" data-mp="3tmuzoz0qh866">
                    <label className="text-xs font-bold text-slate-500 uppercase" data-id="element-500" data-mp="3to65f4dqopzx">
                      Return Method
                    </label>
                    <div className="grid grid-cols-2 gap-2" data-id="element-501" data-mp="1z8tr3mfdt6sw">
                      <button onClick={() => setReturnMethod('Driver')} className={cn('py-3 rounded-xl border-2 text-xs font-bold transition-all', returnMethod === 'Driver' ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-slate-100 text-slate-500 hover:border-slate-200')} data-id="element-502" data-mp="1zaelnc1p4pqr">
                        Request Driver
                      </button>
                      <button onClick={() => setReturnMethod('Customer')} className={cn('py-3 rounded-xl border-2 text-xs font-bold transition-all', returnMethod === 'Customer' ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-slate-100 text-slate-500 hover:border-slate-200')} data-id="element-503" data-mp="cjyx7a1lg5sh">
                        Customer Return
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-504" data-mp="3ckl089bvf5z1">
                  {items.map(item => <div key={item.id} className="p-4 bg-slate-50 rounded-xl flex items-center justify-between" data-id="element-505" data-mp="38gmz5t2zjl3c">
                      <div className="flex items-center gap-3" data-id="element-506" data-mp="2yk1td5j565yg">
                        <img src={item.image} className="w-10 h-10 rounded-lg" data-id="element-507" data-mp="2yllb70kg4p2c" />
                        <p className="text-sm font-bold" data-id="element-508" data-mp="2ouqltnbwl9co">{item.name}</p>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-509" data-mp="2kt51xi0ybusz">
                        <button className="w-8 h-8 rounded border border-slate-200" data-id="element-510" data-mp="2fsr5yycmkbbb">
                          -
                        </button>
                        <span className="text-sm font-bold" data-id="element-511" data-mp="2av8x17ylxw3b">0</span>
                        <button className="w-8 h-8 rounded border border-slate-200" data-id="element-512" data-mp="215sg37tf1oyp">
                          +
                        </button>
                        <span className="text-xs text-slate-400" data-id="element-513" data-mp="266pynlic8l0q">
                          / {item.quantity}
                        </span>
                      </div>
                    </div>)}
                </div>
                <button disabled={!returnReason || returnReasonRequiresText && !returnCustomReason.trim()} onClick={() => {
              const nextStatus = returnMethod === 'Driver' ? 'Waiting Return Confirmation' : 'Return Requested';
              onUpdateStatus(nextStatus, {
                returnReason: returnReasonRequiresText ? returnCustomReason.trim() : returnReason
              });
              setReturnCustomReason('');
              setShowReturnModal(false);
              import('sonner').then(({
                toast
              }) => toast.success(returnMethod === 'Driver' ? 'Driver requested for return. Waiting for item arrival.' : 'Return initiated. Please scan items when received.'));
            }} className="w-full py-4 bg-rose-600 text-white font-bold rounded-2xl shadow-lg disabled:opacity-50" data-id="element-514" data-mp="2jwtgm0a6pskd">
                  Confirm Return ({returnMethod})
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Rx Modal */}
      <AnimatePresence data-id="element-515" data-mp="m74qw376pnpa">
        {showRxModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-516" data-mp="i6kresz527zz">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl" data-id="element-517" data-mp="1xmtaqlp566lv">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-purple-50/50" data-id="element-518" data-mp="w8iw3h9k4ea">
                <h3 className="font-bold text-slate-900" data-id="element-519" data-mp="5qurjyvyniuo">
                  Prescription Attachment
                </h3>
                <button onClick={() => setShowRxModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-520" data-mp="ep6u8za3q73h">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-521" data-mp="jmq4zp7lc1qo" />
                </button>
              </div>
              <div className="p-8" data-id="element-522" data-mp="oljktoaco4u0">
                <div className="aspect-[4/3] bg-slate-100 rounded-2xl border-2 border-slate-200 overflow-hidden" data-id="element-523" data-mp="2lgxvwa8802i9">
                  {order.prescription?.image ? <img src={order.prescription.image} alt="Electronic prescription" className="w-full h-full object-contain" data-id="element-524" data-mp="2qh3optq87v1x" /> : <div className="flex flex-col items-center justify-center h-full text-slate-400" data-id="element-525" data-mp="yr1a6hp760df">
                      <Globe className="w-12 h-12 mb-2" data-id="element-526" data-mp="ttstymnm06et" />
                      <p className="font-bold" data-id="element-527" data-mp="1qj0f3tkbgdi7">No Image Attached</p>
                    </div>}
                </div>
                <div className="mt-6 flex gap-3" data-id="element-528" data-mp="1mh5gwxxomg8n">
                  <button onClick={() => setShowRxModal(false)} className="flex-1 rounded-2xl border border-slate-200 bg-white py-4 font-bold text-slate-600" data-id="element-529" data-mp="1mfawrlu8is5m">
                    Close
                  </button>
                  {order.prescription && !order.prescription.verified && <button onClick={() => {
                onUpdateStatus(order.status, {
                  prescription: {
                    ...order.prescription,
                    verified: true
                  }
                });
                setShowRxModal(false);
              }} className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-4 font-bold text-white shadow-lg shadow-emerald-100 transition-colors hover:bg-emerald-700" data-id="element-530" data-mp="13suj3bvnljc">
                      <CheckCircle2 className="h-4 w-4" data-id="element-531" data-mp="3e6qowklrfmhe" />
                      Approve eRx
                    </button>}
                  <button onClick={() => {
                setShowRxModal(false);
                setShowHoldModal(true);
                setHoldReason('Prescription Not Valid');
              }} className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-amber-500 py-4 font-bold text-white shadow-lg shadow-amber-100 transition-colors hover:bg-amber-600" data-id="element-532" data-mp="3n8zvizdwqvpi">
                    <Clock className="h-4 w-4" data-id="element-533" data-mp="1eqq7v7bkojfq" />
                    Hold eRx
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Hold Modal */}
      <AnimatePresence data-id="element-534" data-mp="14xsutgpcnmoi">
        {showHoldModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-535" data-mp="19xfadwsnhf0h">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-536" data-mp="v04rw3hlb8v9">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-537" data-mp="2gcy3r2g2qhgk">
                <h3 className="font-bold text-slate-900" data-id="element-538" data-mp="2lbj55572b76l">Hold Order</h3>
                <button onClick={() => setShowHoldModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-539" data-mp="2j0s025yecoa9">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-540" data-mp="29xqvwat3d798" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-541" data-mp="250h9l4101bpm">
                <div className="space-y-2" data-id="element-542" data-mp="251sc70ipkdf5">
                  {holdReasons.map(reason => <button key={reason.id} onClick={() => {
                setHoldReason(reason.label);
                if (!reason.requiresFreeText) setHoldCustomReason('');
              }} className={cn('w-full px-4 py-3 rounded-xl border-2 text-left text-sm font-medium transition-all', holdReason === reason.label ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200')} data-id="element-543" data-mp="32pagnz5c6tdc">
                      {reason.label}
                    </button>)}
                  {holdReasonRequiresText && <textarea value={holdCustomReason} onChange={event => setHoldCustomReason(event.target.value)} placeholder="Enter the hold reason..." className="min-h-[88px] w-full rounded-xl border-2 border-slate-100 bg-slate-50 p-4 text-sm outline-none transition-colors focus:border-slate-800 focus:bg-white" data-id="element-544" data-mp="1t9u720jqj32" />}
                </div>
                <button disabled={!holdReason || holdReasonRequiresText && !holdCustomReason.trim()} onClick={() => {
              onUpdateStatus('Held', {
                holdReason: holdReasonRequiresText ? holdCustomReason.trim() : holdReason
              });
              setHoldCustomReason('');
              setShowHoldModal(false);
            }} className="w-full py-4 bg-slate-800 text-white font-bold rounded-2xl shadow-lg disabled:opacity-50" data-id="element-545" data-mp="pfyji8rvxdtp">
                  Confirm Hold
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Patient Share Collection Modal */}
      <AnimatePresence data-id="element-546" data-mp="188n43ozl9eg9">
        {showPatientShareModal && order.insurance && <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-id="element-547" data-mp="1m7v4gl7hmsm1">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="patient-share-title" data-id="element-548" data-mp="1h8a5iasbik9b">
              <div className="flex items-center justify-between border-b border-violet-100 bg-violet-50/70 p-6" data-id="element-549" data-mp="357m2hsdcnpn6">
                <div className="flex items-center gap-3" data-id="element-550" data-mp="2r8359um2typn">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100 text-violet-700" data-id="element-551" data-mp="2w5micr71g5g0"><CreditCard className="h-5 w-5" data-id="element-552" data-mp="3jvpbzbqlpjgq" /></div>
                  <div data-id="element-553" data-mp="3jw8qsjkyfrqj">
                    <h3 id="patient-share-title" className="font-bold text-slate-900" data-id="element-554" data-mp="3ouk26yqweaav">Collect Patient Share</h3>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-violet-700" data-id="element-555" data-mp="3a5xarynzxs3a">Insurance walk-in</p>
                  </div>
                </div>
                <button onClick={() => setShowPatientShareModal(false)} className="rounded-full p-1 text-slate-400 transition-colors hover:bg-white" aria-label="Close patient share collection dialog" data-id="element-556" data-mp="3f2nrmo7mkrpr"><XCircle className="h-5 w-5" data-id="element-557" data-mp="bh88uiffhzgj" /></button>
              </div>
              <div className="space-y-5 p-8" data-id="element-558" data-mp="lakwlaq3o0bl">
                <div className="rounded-2xl border border-violet-100 bg-violet-50/60 p-5 text-center" data-id="element-559" data-mp="l9jhmv5yqtol">
                  <p className="text-[10px] font-black uppercase tracking-widest text-violet-600" data-id="element-560" data-mp="gaq638a8lcr2">Amount due from patient</p>
                  <p className="mt-2 text-3xl font-black text-slate-900" data-id="element-561" data-mp="7c4njd4rlxqa">{order.insurance.patientShare.toFixed(2)} SAR</p>
                  <p className="mt-2 text-xs text-slate-500" data-id="element-562" data-mp="2dtgfa5uu1br">{order.insurance.payerName} covers {order.insurance.insuranceShare.toFixed(2)} SAR · {order.insurance.copayPercent}% copay</p>
                </div>
                <p className="text-center text-sm leading-6 text-slate-600" data-id="element-563" data-mp="3ot4fvnqsvvu9">Confirm collection at the counter to release this order for picking.</p>
              </div>
              <div className="flex gap-3 border-t border-slate-100 bg-slate-50 p-6" data-id="element-564" data-mp="3frem4b9g4ktb">
                <button onClick={() => setShowPatientShareModal(false)} className="flex-1 rounded-xl py-3.5 font-bold text-slate-600 transition-colors hover:bg-white" data-id="element-565" data-mp="3asl7gfifanrk">Cancel</button>
                <button onClick={() => {
              onUpdateStatus('Picking', {
                insurance: {
                  ...order.insurance,
                  paymentLinkStatus: 'Paid'
                }
              });
              setShowPatientShareModal(false);
            }} className="flex-1 rounded-xl bg-emerald-600 py-3.5 font-bold text-white shadow-lg shadow-emerald-100 transition-colors hover:bg-emerald-700" data-id="element-566" data-mp="1qcna5b5t3s87">
                  Collect & Start Picking
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Yaser Label Preview */}
      <AnimatePresence data-id="element-567" data-mp="1lbfxaxot42uo">
        {showLabelModal && scanningItem && fulfillmentProfile.requiresPrescriptionReview && <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-id="element-568" data-mp="nwlo74hddwzx">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="w-full max-w-sm overflow-hidden rounded-3xl bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="label-preview-title" data-id="element-569" data-mp="su3ns49hyzv9">
              <div className="flex items-center justify-between border-b border-purple-100 bg-purple-50/70 p-6" data-id="element-570" data-mp="3rey7et5k61gg"><div data-id="element-571" data-mp="3nb8mqes0840p"><h3 id="label-preview-title" className="font-bold text-slate-900" data-id="element-572" data-mp="3nbifga4fopa3">Dispensing label</h3><p className="text-[10px] font-bold uppercase tracking-widest text-purple-700" data-id="element-573" data-mp="55x0ur2so2jt">Yaser eRx</p></div><button onClick={() => setShowLabelModal(false)} className="rounded-full p-1 text-slate-400 hover:bg-white" aria-label="Close label preview" data-id="element-574" data-mp="8dm7q26pnrc"><XCircle className="h-5 w-5" data-id="element-575" data-mp="evow7nipwzpc" /></button></div>
              <div className="m-6 space-y-4 rounded-2xl border-2 border-dashed border-slate-200 p-5 text-sm text-slate-700" data-id="element-576" data-mp="9x5bfqfv1tyk"><p className="font-black text-slate-900" data-id="element-577" data-mp="ny7yo6ildab2">{scanningItem.dispensedTradeDrugCode || scanningItem.tradeDrugCode}</p><p data-id="element-578" data-mp="izenikqs1ve2">{scanningItem.name}</p><p className="font-mono text-xs" data-id="element-579" data-mp="iw0j76yjzc3z">Lot: {scanningItem.selectedLotNumber || 'Select during picking'}</p><p className="border-t border-slate-100 pt-3 text-xs leading-5" data-id="element-580" data-mp="12hsj2fxub7c0">{scanningItem.instructionsAr || 'Use as directed by your pharmacist.'}</p><p className="text-[10px] font-bold uppercase text-slate-400" data-id="element-581" data-mp="tjqexrit7yki">Patient: {order.customer.name}</p></div>
              <div className="flex gap-3 border-t border-slate-100 bg-slate-50 p-6" data-id="element-582" data-mp="xjr2q2rstm48"><button onClick={() => setShowLabelModal(false)} className="flex-1 rounded-xl py-3.5 font-bold text-slate-600 hover:bg-white" data-id="element-583" data-mp="jtnmptjiion8">Close</button><button onClick={() => window.print()} className="flex-1 rounded-xl bg-purple-600 py-3.5 font-bold text-white shadow-lg shadow-purple-100 transition-colors hover:bg-purple-700" data-id="element-584" data-mp="okvxaswkwg1z">Print label</button></div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* OTP Modal */}
      <AnimatePresence data-id="element-585" data-mp="v1k2a8ckph7">
        {showOtpModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-586" data-mp="5u4fd2yizhbz">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-587" data-mp="40g67h3bgkxe">
              <div className="flex items-center justify-between border-b border-slate-100 p-6" data-id="element-588" data-mp="np8jthtpxxfo">
                <h3 className="font-bold text-slate-900" data-id="element-589" data-mp="rp0mjdwfl9d4">{fulfillmentProfile.requiresNationalIdVerification ? 'National ID Verification' : 'OTP Verification'}</h3>
                <button onClick={() => setShowOtpModal(false)} className="rounded-full p-1 transition-colors hover:bg-slate-100" aria-label="Close pickup verification dialog" data-id="element-590" data-mp="ixgq4yf9nblb">
                  <XCircle className="h-5 w-5 text-slate-400" data-id="element-591" data-mp="1beayqhwyxc6h" />
                </button>
              </div>
              <div className="space-y-6 p-8" data-id="element-592" data-mp="1kt37abk7cwfz">
                <div className="rounded-2xl border border-purple-100 bg-purple-50 p-4 text-center" data-id="element-593" data-mp="1krs4of2htuqg">
                  <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-white text-purple-600 shadow-sm" data-id="element-594" data-mp="1gqq2bpda52k9"><User className="h-5 w-5" data-id="element-595" data-mp="1ui4mw0mu5btf" /></div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-purple-600" data-id="element-596" data-mp="1pjjng3feqw61">{fulfillmentProfile.requiresNationalIdVerification ? 'Yaser eRx Pickup' : 'Self Pickup'}</p>
                  <p className="mt-1 text-sm font-bold text-slate-900" data-id="element-597" data-mp="xt5fsoxzmg2t">{order.customer.name} is collecting this order</p>
                </div>

                {fulfillmentProfile.requiresNationalIdVerification ? <>
                    <div className="space-y-2 text-center" data-id="element-598" data-mp="16rqug90c9ayb">
                      <ShieldCheck className="mx-auto h-8 w-8 text-purple-600" data-id="element-599" data-mp="2n725y5yvfcn8" />
                      <h4 className="text-lg font-bold" data-id="element-600" data-mp="2i8h4k37vumx7">Verify National ID</h4>
                      <p className="text-sm text-slate-500" data-id="element-601" data-mp="2ial64eib79ub">Match the patient’s national ID before releasing this Yaser eRx order.</p>
                    </div>
                    <input autoFocus inputMode="numeric" type="text" value={nationalIdValue} onChange={event => setNationalIdValue(event.target.value.replace(/\D/g, ''))} placeholder="National ID" aria-label="Patient national ID" className="w-full rounded-2xl border-2 border-slate-100 bg-slate-50 px-5 py-4 text-center font-mono text-xl font-black tracking-wider outline-none transition-colors focus:border-purple-500 focus:bg-white" data-id="element-602" data-mp="2u9qlvgcryif8" />
                    <div className="space-y-2" data-id="element-603" data-mp="2lc6hhyjm860e">
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-500" data-id="element-604" data-mp="1nvhisdpypjv8">Patient mobile (optional)</label>
                      <input inputMode="tel" type="tel" value={pickupMobileValue} onChange={event => setPickupMobileValue(event.target.value.replace(/\D/g, ''))} placeholder={order.customer.mobile} aria-label="Optional patient mobile number" className="w-full rounded-xl border-2 border-slate-100 bg-slate-50 px-4 py-3 text-sm font-medium outline-none transition-colors focus:border-purple-500 focus:bg-white" data-id="element-605" data-mp="1ss7zn39lcjhp" />
                    </div>
                    <button disabled={nationalIdValue !== order.customer.nationalId} onClick={() => {
                onUpdateStatus('Delivered', {
                  customer: {
                    ...order.customer,
                    mobile: pickupMobileValue || order.customer.mobile
                  }
                });
                setShowOtpModal(false);
              }} className="w-full rounded-2xl bg-purple-600 py-4 font-bold text-white shadow-lg shadow-purple-100 transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-606" data-mp="xiujitslo4xf">
                      Verify ID & Complete
                    </button>
                  </> : <>
                    <div className="space-y-2 text-center" data-id="element-607" data-mp="yjxuxt9k3wdn">
                      <Smartphone className="mx-auto h-8 w-8 text-blue-600" data-id="element-608" data-mp="klq1hvbqbd78" />
                      <h4 className="text-lg font-bold" data-id="element-609" data-mp="pksxkq9qe932">Confirm Pickup with OTP</h4>
                      <p className="text-sm text-slate-500" data-id="element-610" data-mp="pipzdiyfd9n9">Ask the customer for the 4-digit collection code sent to {order.customer.mobile}.</p>
                    </div>
                    <input autoFocus inputMode="numeric" type="text" maxLength={4} value={otpValue} onChange={event => setOtpValue(event.target.value.replace(/\D/g, ''))} aria-label="Four digit pickup OTP" className="w-full rounded-2xl border-2 border-slate-100 bg-slate-50 py-6 text-center text-4xl font-black tracking-widest outline-none transition-colors focus:border-blue-500 focus:bg-white" data-id="element-611" data-mp="fneppf1vismd" />
                    <div className="flex items-center justify-center gap-4 text-xs font-bold" data-id="element-612" data-mp="3b42lh4wd0fb">
                      <button onClick={handleSendWhatsApp} className="text-blue-600 transition-colors hover:text-blue-800" data-id="element-613" data-mp="i0056yo6pl8j">Send via WhatsApp</button>
                      <span className="h-3 border-l border-slate-200" data-id="element-614" data-mp="r1zoxyql33f6" />
                      <button disabled={!canResend} onClick={handleResendOtp} className="text-blue-600 transition-colors hover:text-blue-800 disabled:cursor-not-allowed disabled:text-slate-400" data-id="element-615" data-mp="r05c77pguhm6">
                        {canResend ? 'Resend OTP' : `Resend in ${resendTimer}s`}
                      </button>
                    </div>
                    <button disabled={otpValue.length !== 4} onClick={() => {
                if (otpValue === order.otp) {
                  onUpdateStatus('Delivered', {
                    otpVerified: true
                  });
                  setShowOtpModal(false);
                } else {
                  import('sonner').then(({
                    toast
                  }) => toast.error('Invalid OTP'));
                }
              }} className="w-full rounded-2xl bg-blue-600 py-4 font-bold text-white shadow-lg shadow-blue-100 transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50" data-id="element-616" data-mp="rrn2zay0kfdt">
                      Verify & Complete
                    </button>
                  </>}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}